import { useTranslation } from "react-i18next"
import WhiteSpace from "../../components/Whitespace"
import PageTitle from "../../components/PageTitle"
import styled from "styled-components"
import Select from "../../components/Select"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import { useEffect, useState } from "react"
import Comm from "../../Comm"
import ChargerType from "../../types/ChargerType"
import Modal from "../../components/Modal"
import Slider from "../../components/Slider"
import Switch from "../../components/Switch"
import Button from "../../components/Button"
import Icon from "../../Icon"
import Navigator from "../../components/Navigator"
import LoaderDots from "../../components/LoaderDots"
import Table from "../../modules/Table"
import { TableCellTypes } from "../../enums/TableCellTypes"
import InputWithLabel from "../../components/InputWithLabel"
import { useConfirm } from "../../ConfirmProvider"
import { child, get, getDatabase, ref, set } from "firebase/database"
import fbConf from "../../FirebaseConf"
import Dropdown from "../../components/Dropdown"
import Input from "../../components/Input"
import { useNotification } from "../../modules/Notification"
import { BREAKPOINTS } from "../../GlobalTheme"
import IntegrationsModal from "./integrations/IntegrationsModal"
import OcppModal from "./integrations/OcppModal"
import PowerShareStatisticsModal from "./PowerShareStatisticsModal"

const METER_TYPE_OPTIONS = ["Disabled", "Carlo Gavazzi EMxxx", "Smart EVSE SensorBox", "Carlo Gavazzi EMxxx (MID)", "Inepro Pro380", "Inepro Pro380 (MID)", "ORNO OR-WE-516", "ORNO OR-WE-516 (MID)", "Voltie Sensor", "Huawei DTSU666", "EMK_FROM_PI", "ORNO OR-WE-525 (MID)", "Siemens Sentron PAC3100"]
const METER_USAGE_OPTIONS = ["None", "PS Usage", "OCPP Usage", "MID Usage", "EVSE Usage"]

const DISPLAY_LANGUAGES = [
  { id: 0, label: "English", icon: "flag.EN" },
  { id: 1, label: "Magyar", icon: "flag.HU" },
  { id: 2, label: "Slovenčina", icon: "flag.SK" },
  { id: 3, label: "Deutsch", icon: "flag.DE" },
  { id: 4, label: "Română", icon: "flag.RO" },
  { id: 5, label: "Italiano", icon: "flag.IT" },
  { id: 6, label: "Slovenščina", icon: "flag.SL" },
  { id: 7, label: "Polski", icon: "flag.PL" },
  { id: 8, label: "Español", icon: "flag.ES" },
  { id: 9, label: "Português", icon: "flag.PT" },
  { id: 10, label: "Türkçe", icon: "flag.TR" },
]
const DISPLAY_LANGUAGE_LABELS = DISPLAY_LANGUAGES.map(l => l.label)

const MID_METER_TYPE_MAP = new Map<number, string>([
  [3, "Carlo Gavazzi EMxxx w/ Modbus"],
  [5, "Inepro Pro380"],
  [7, "ORNO OR-WE-516"],
  [11, "ORNO OR-WE-525"],
])

interface ChargerValuesType {
  power_stat: {
    current1: number,
    current2: number,
    current3: number,
    power1: number,
    power2: number,
    power3: number,
    voltage1: number,
    voltage2: number,
    voltage3: number,
    temp_uc: number,
    temp_pow: number,
    temp_pi: number,
    hum: number,
    dlm_valid: boolean,
    dlm_current1: number,
    dlm_current2: number,
    dlm_current3: number,
    ipm_valid: boolean,
    ipm_current1: number,
    ipm_current2: number,
    ipm_current3: number
  },
  config: {
    wifi: {
      mode: number,
      ssid: string,
      passwd: string,
      ap_mode_ssid: string
    },
    ethernet: {
      mode: number,
      ip_address: string,
      netmask: string,
      gateway: string,
      dns: string
    },
    gsm: {
      mode: number,
      apn: string,
      apn_uname: string,
      apn_pwd: string,
      sim_pin: string,
      pin_error: boolean,
      nw_reg_to_sec: number,
      IDs: {
        iccid: string,
        imei: string,
        imsi: string,
        revision: string
      }
    },
    display: {
      enabled: boolean,
      night_mode_en: boolean,
      night_period_sp: number,
      night_period_st: number,
      on_seconds: number,
      rotate180: boolean,
      language: number,
      eyes_en: boolean,
      tap_rfid_interval: number,
      brightness: number,
      scroll_speed: number,
      idle_text: string,
      idle_text_interval: number
    },
    front_led: {
      enabled: boolean,
      brightness: number,
      night_mode_en: boolean,
      night_period_st: number,
      night_period_sp: number
    },
    rear_led: {
      enabled: boolean,
      brightness: number,
      night_mode_en: boolean,
      night_period_st: number,
      night_period_sp: number,
      color_r: number,
      color_g: number,
      color_b: number
    },
    sound: {
      enabled: boolean,
      night_mode_en: boolean,
      night_period_st: number,
      night_period_sp: number
    },
    localisation: {
      gmt_offset: number,
      zoneinfo: string
    },
    power: {
      phase_order: number,
      curr_usr_limit: number,
      curr_night_limit: number,
      night_mode_en: boolean,
      night_period_st: number,
      night_period_sp: number,
      mid_meter_en: boolean,
      mid_meter_type: number,
      dlm_mode: number,
      dlm_meter_type: number,
      dlm_dyn_curr_lim: number,
      dlm_dyn_specific: boolean,
      dlm_dyn_curr1_lim: number,
      dlm_dyn_curr2_lim: number,
      dlm_dyn_curr3_lim: number,
      dlm_dyn_ud_time: number,
      dlm_dyn_dd_time: number,
      dlm_ecogrn_id_time: number,
      dlm_grid_u_stop: number,
      dlm_grid_u_min: number,
      dlm_grid_u_max: number,
      dlm_grid_t_up: number,
      dlm_grid_t_dn: number,
      curr_hw_limit: number,
      mid_meter_state: number,
      dlm_meter_state: number,
      night_mode: boolean,
      curr_hw_limit_min: number
    },
    ip_485: {
      ip: string,
      port: number,
      meter1: {
        type: number,
        usage: number,
        sn: string
      },
      meter2: {
        type: number,
        usage: number,
        sn: string
      }
    },
    pi2pi_netw: {
      role: number,
      port: number,
      master1_ip: string,
      master2_ip: string,
      top_master1_ip: string,
      top_master2_ip: string,
      segment_id: number,
      ps_enabled: boolean,
      ps_autonom_curr: number
    }
  },
  rsp_stat: {
    wifi?: {
      mode: number,
      rssi: number,
      signal_percent: number,
      mac: string,
      ip: string,
      ssid: string,
      ap_mode_ssid: string,
      freq: string,
      rx_bitrate: string,
      tx_bitrate: string,
      hidden_ssid: boolean,
      hide_ap: boolean
    },
    ethernet?: {
      mode: number,
      ip_address: string,
      netmask: string,
      gateway: string,
      dns: string,
      hostname: string,
      mac_address: string,
      speed_mbps: number
    },
    gsm?: {
      mode: number,
      status: string,
      imei: string,
      iccid: string,
      uptime: number,
      rssi: string,
      rx_bytes: string,
      tx_bytes: string,
      operator: string
    }
  },
  ps_conf: {
    ext_meter: {
      i_offline_r: number,
      i_offline_s: number
      i_offline_t: number,
      imax_r: number,
      imax_s: number,
      imax_t: number,
      reserve_r: number,
      reserve_s: number,
      reserve_t: number,
    },
    l2: {
      i_autonom_r: number,
      i_autonom_s: number,
      i_autonom_t: number,
    },
    ps_conf_ver: number,
    ps_general: {
      conf_date: number,
      imax_r: number,
      imax_s: number,
      imax_t: number,
      n_endpoints: number,
      ps_method: number,
      reserve_amps_each_na: number,
      reserve_r: number,
      reserve_s: number,
      reserve_t: number
    }
  },
  status: {
    msg_type: number,
    sys_time: number,
    evse_state: number,
    phase: number,
    u_eff: number,
    chg_on: boolean,
    chg_current: number,
    chg_power: number,
    srs: number,
    chg_enabled: boolean,
    autostart: number,
    temp_uc: number,
    temp_pow: number,
    temp_pi: number,
    hum: number,
    first_cdr: number,
    last_cdr: number,
    max_current: number
  }
}

const Container = styled.div`
  width: 100%;
  height: calc(90vh - 70px);
  overflow-y: scroll;

  /* The cards stack below the breakpoint and the page itself scrolls, so a nested scroll region
     here would only trap the gesture. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    overflow: visible;
  }
`

/* The card grids are five 20% columns in a single non-wrapping row, so they need 1000px and on a
   phone could only be reached by swiping sideways. Stack them below the phone breakpoint. The td
   widths are inline styles, which is why overriding them needs !important. */
const SettingsGrid = styled.table`
  width: 100%;

  /* Five fixed 20% columns is the wrong shape: at 1280 that leaves ~88px for a label plus its
     chevron, so "Connections" and "Charger Enabled" print under their own controls - visible in the
     pre-change reference too. An auto-fitting grid keeps the card width roughly constant and varies
     the COUNT instead: 5 across at 1920 as before, 3 at 1280. The cells carry inline percentage
     widths, hence !important. */
  @media (min-width: ${BREAKPOINTS.shell + 1}px) {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));

    tbody,
    tr {
      display: contents;
    }

    td {
      display: block;
      width: auto !important;
    }
  }

  @media (max-width: ${BREAKPOINTS.phone}px) {
    display: block;

    tbody, tr, td {
      display: block;
      width: auto !important;
    }
  }
`

const SettingContainer = styled.div<{ $color?: string, $noAnimation?: boolean, $noFlex?: boolean, $noCursorPointer?: boolean }>`
  background-color: ${props => props.$color ? props.theme.colors[props.$color] : 'transparent'};
  padding: 24px;
  margin: 8px;
  display: ${props => props.$noFlex ? 'block' : 'flex'};
  border-radius: 24px;
  position: relative;
  cursor: ${props => props.$noCursorPointer ? 'auto' : 'pointer'};

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* The arrow is a flow sibling now, so it needs no reserved strip - 60px here just left a dead
       band between it and the card edge. */
    padding-right: 24px;
    /* the row is nowrap by default, so a static full-width child cannot break onto its own line */
    flex-wrap: wrap;

    /* The bottom charger-action row is the only group with no card colour. Stacked, its 24px card
       padding turned seven one-line actions into ~105px-tall blocks of empty space; as a plain list
       row each is a comfortable tap target without the card metrics. */
    ${props => !props.$color && `
      padding: 10px 16px;
      margin: 0px;
      align-items: center;
    `}
  }

  &:active {
    ${props => !props.$noAnimation && `
      transform: scale(.985);
    `}
  }
`

const SettingIconContainer = styled.div<{ $small?: boolean }>`
  width: ${props => props.$small ? '25px' : '40px'};
  height: ${props => props.$small ? '25px' : '40px'};
  padding: ${props => props.$small ? '7.5px' : '0px'};
`

const SettingLabel = styled.p<{ $white?: boolean, $small?: boolean }>`
  font-weight: 700;
  font-size: ${props => props.$small ? '14px' : '18px'};
  color: ${props => props.$white ? props.theme.colors.white : props.theme.colors.dark};
  margin: 0px;
  margin-top: ${props => props.$small ? '10px' : '8px'};
  margin-left: 12px;

  /* The top margin centres the label against the icon in a card; in the stacked list rows the row
     itself centres them, so it only adds height. */
  @media (max-width: ${BREAKPOINTS.phone}px) {
    margin-top: ${props => (props.$small ? '0px' : '8px')};
  }
  /* The arrow is a flow sibling, so the label just shrinks and wraps against it. Reserving a fixed
     width here instead inflated each cell's min-content and pushed the 5-column grid to 1482px,
     which overflowed a 1280px window horizontally. */
  min-width: 0;
`

/* Header row for the Custom commands card. The action button used to be absolutely anchored to the
   card's right edge, which put it on top of the title once the card narrowed. In flow with
   margin-left:auto it sits in the same place while there is room and wraps below when there is not. */
const CustomCommandsHeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`

const CustomCommandActionSlot = styled.div`
  margin-left: auto;
  margin-right: 20px;
  margin-top: 4px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* width:100% and an auto left margin would together demand more than the row has. */
    margin-left: 0px;
    margin-right: 0px;
    margin-top: 12px;
    width: 100%;
  }
`

/* The charger-enabled toggle is wider than the 44px the label reserves for a chevron, so it goes
   in flow with an auto left margin: same position where there is room, and the label wraps rather
   than running underneath it where there is not. */
const SettingSwitchSlot = styled.div`
  margin-left: auto;
  margin-top: 4px;
  flex: 0 0 auto;
`

/* In flow with an auto left margin rather than anchored to the card's right edge: it lands in the
   same place while there is room, and the label wraps against it rather than under it when there
   is not. An absolutely positioned arrow cannot be cleared by card padding, because an absolute
   child resolves against the padding box. */
const SettingArrow = styled.div`
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  margin-left: auto;
  margin-top: 10px;
`

const DividerLineColor = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 4px;
  background-image: linear-gradient(to right, #2A6FF4, #E2594B);
`

const DividerLineGray = styled.div`
  width: 100%;
  height: 1px;
  border-radius: 1px;
  background-color: ${props => props.theme.colors.gray_300};
`

const CustomCommandsIconBackground = styled.div`
  background-color: ${props => props.theme.colors.dark};
  height: 40px;
  width: 40px;
  border-radius: 10px;
`

const CustomCommandsTextInput = styled.textarea`
  width: 100%;
  height: 100px;
  resize: none;
  outline: none;
  border: 1px solid ${props => props.theme.colors.gray_300};
  border-radius: 8px;
`

const ModalTitle = styled.p`
  font-weight: 600;
  font-size: 20px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
  width: min(max(20vw, 256px), calc(100vw - 24px));
`

const ModalSubcategoryTitle = styled.p` // When there is a back icon before the label
  font-weight: 600;
  font-size: 20px;
  margin: 0px;
  color: ${props => props.theme.colors.gray_900};
  width: calc(min(max(20vw, 256px), calc(100vw - 24px)) - 22px - 8px);
`

const ModalContainer = styled.div`
  padding: 24px;
  height: 85dvh;
  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* A fixed height with no overflow of its own caps the parent Modal's scroll area, so anything
       past it is clipped AND unreachable. Size to content and let Modal's max-height scroll it. */
    height: auto;
  }
`

const ModalCloseButtonContainer = styled.div`
  position: absolute;
  top: 26px;
  right: 26px;
`

const ModalButtonContainer = styled.div`
  position: absolute;
  bottom: 0px;
  left: 0px;
  width: calc(100% - 10px);
  background-color: ${props => props.theme.colors.background};
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;

  /* The modal is a fixed 85dvh above the breakpoint, so an anchored footer always has room below the
     content. Below it the modal sizes to its content and the footer printed straight onto the last
     rows - Cancel over "MID meter enabled", Refresh over the phase values. In flow instead. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    width: 100%;
    margin-top: 20px;
  }
`

const ModalButton = styled.p<{ $hasBg?: boolean }>`
  font-weight: 600;
  font-size: 15px;
  color: ${props => props.$hasBg ? props.theme.colors.white : props.theme.colors.gray_500};
  padding-top: 8px;
  padding-bottom: 8px;
  margin: 16px;
  width: 20%;
  text-align: center;
  border-radius: 12px;
  float: right;
  background-color: ${props => props.$hasBg ? props.theme.colors.dark : 'none'};
  cursor: pointer;
`

const ModalDetailTitle = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
`

const ModalDetailTitleThin = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-top: 4px;
`

const ModalDetailDivider = styled.div<{ $isColor?: boolean }>`
  height: ${props => props.$isColor ? '2px' : '1px'};
  width: 100%;
  margin-top: 18px;
  margin-bottom: 18px;
  background-color: ${props => props.$isColor ? 'none' : props.theme.colors.gray_300};
  background-image: ${props => props.$isColor ? 'linear-gradient(to right, #2A6FF4, #E2594B)' : 'none'};
`


const DetailedStatsValueUnit = styled.span`
  font-weight: 500;
  font-size: 15px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
  margin-left: 2px;
`

const TimeInput = styled.input<{ $right?: boolean }>`
  border: 1px solid ${props => props.theme.colors.gray_400};
  border-radius: 8px;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 6px;
  padding-bottom: 6px;
  outline: none;
  position: ${props => props.$right ? 'absolute' : 'relative'};
  right: ${props => props.$right ? '20px' : 'auto'};

  /* The end-of-night-period input is absolute, so it is OUT OF FLOW and contributes nothing to the
     modal's scrollHeight - it can therefore render below the visible area with no way to scroll to it.
     Put it back in flow on narrow viewports. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: relative;
    right: auto;
    margin-left: 8px;
  }
`

const ColorInput = styled.input`
  width: 32.5px;
  height: 32.5px;
  outline: none;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &::-webkit-color-swatch {
    border-radius: 15px;
    border: 2px solid ${props => props.theme.colors.gray_400};
  }

  &::-moz-color-swatch {
    border-radius: 30px;
    border: 2px solid ${props => props.theme.colors.gray_400};
  }
`

/* The diagnostic table's columns are fixed at 150-300px, far wider than a phone-sized modal, and the
   Modal shell clips horizontally - so ~888px of it was simply unreachable. Pan it here instead. */
const ModalTableScroller = styled.div`
  @media (max-width: ${BREAKPOINTS.shell}px) {
    overflow-x: auto;
    overscroll-behavior-x: contain;
    max-width: 100%;
  }
`

/* The three phase readings belong side by side at every width. They previously reused SettingsGrid,
   which stacks its cells on a phone, so the values cascaded diagonally down the modal. */
const PhaseValueRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: baseline;

  > span {
    font-weight: 700;
    font-size: 24px;
    color: ${props => props.theme.colors.gray_900};
  }

  > span:nth-child(2) { text-align: center; }
  > span:last-child:not(:first-child) { text-align: right; }

  @media (max-width: ${BREAKPOINTS.phone}px) {
    > span {
      font-size: 20px;
    }
  }
`

const ModalDetailRow = styled.div`
  display: flex;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 0px;
  }
`

/* Sits at the row's right edge where there is room, and wraps under it where there is not. */
const ModalDetailTrailing = styled.div`
  display: flex;
  margin-left: auto;
  margin-right: 20px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    margin-right: 0px;
    padding-left: 16px;
  }
`

const ConnectionActiveLabel = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.primary};
  position: absolute;
  right: 52px;
  margin-top: 10px;

  /* Pinned to the row's right edge, which on a narrow modal is on top of the row's own label
     ("Wifi Settingsctive"). In flow, pushed right by its margin instead. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    right: auto;
    margin-left: auto;
    margin-right: 8px;
    align-self: center;
  }
`

// Power sharing statistics live on the masters of a Pi2Pi network: Pi2Pi on (role 0 is off), any master
// role (7 is a client) and power sharing enabled (VLT-2157).
const PI2PI_OFF = 0
const PI2PI_CLIENT = 7
function hasPowerShareStatistics(pi2pi?: { role: number, ps_enabled: boolean }): boolean {
  return !!pi2pi && pi2pi.role !== PI2PI_OFF && pi2pi.role !== PI2PI_CLIENT && pi2pi.ps_enabled
}

export default function Settings({ charger, isAdmin }: { charger: ChargerType, isAdmin: boolean }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const { addNotification } = useNotification()

  const [chargerValues, setChargerValues] = useState<ChargerValuesType>()
  const [isChargerBlocked, setIsChargerBlocked] = useState<boolean>()

  const [isCustomCommandsCommandEditorOpen, setIsCustomCommandsCommandEditorOpen] = useState<boolean>(false)
  const [customCommandEditorNameInput, setCustomCommandEditorNameInput] = useState<string>("")
  const [customCommandEditorCommandInput, setCustomCommandEditorCommandInput] = useState<string>("")
  const [customCommandEditorDescriptionInput, setCustomCommandEditorDescriptionInput] = useState<string>("")
  const [customCommandInputValue, setCustomCommandInputValue] = useState<string>('')
  const [customCommandOptions, setCustomCommandOptions] = useState<{ [key: string]: { command: { msg_type: string, [key: string]: any }, description: string } } | null>(null)
  const [isEditingCustomCommand, setIsEditingCustomCommand] = useState<string | null>(null)

  const [isShowingDetailedStats, setIsShowingDetailedStats] = useState<boolean>(false)
  const [isShowingDisplaySettings, setIsShowingDisplaySettings] = useState<boolean>(false)
  const [isShowingLightSettings, setIsShowingLightSettings] = useState<boolean>(false)
  const [isShowingSpeakerSettings, setIsShowingSpeakerSettings] = useState<boolean>(false)
  const [isShowingPowerOptions, setIsShowingPowerOptions] = useState<boolean>(false)
  const [isShowingConnections, setIsShowingConnections] = useState<boolean | string>(false) // true / false / wifi / ethernet / gsm
  const [isShowingMidMetering, setIsShowingMidMetering] = useState<boolean>(false)
  const [isShowingOcpp, setIsShowingOcpp] = useState<boolean>(false)
  const [isShowingIntegrations, setIsShowingIntegrations] = useState<boolean>(false)
  const [isShowingIpMeterSettings, setIsShowingIpMeterSettings] = useState<boolean>(false)
  const [isShowingPowerShareSettings, setIsShowingPowerShareSettings] = useState<boolean>(false)
  const [isShowingPowerShareStatistics, setIsShowingPowerShareStatistics] = useState<boolean>(false)

  const [isShowingDebugLevels, setIsShowingDebugLevels] = useState<boolean>(false)
  const [debugLevels, setDebugLevels] = useState<{ name: string, value: number }[] | null>()

  const [isShowingDiagnosticLogConfig, setIsShowingDiagnosticLogConfig] = useState<boolean>(false)
  const [diagnosticLogConfig, setDiagnosticLogConfig] = useState<{ name: string, enabled: boolean, tick10min: boolean, dt_off: number, max_lines_10min: number, param1: number, param2: number }[]>()

  const [PSPhaseOrderTranslationTable] = useState<{ [key: string]: string }>({
    "0": "Unknown",
    "1": "RST",
    "2": "STR",
    "3": "TRS",
    "4": "R Only",
    "5": "S Only",
    "6": "T Only",
    "7": "Simu RS",
    "8": "Simu ST",
    "9": "Simu TR"
  })

  const [PSDeviceRoleTranslationTable] = useState<{ [key: string]: string }>({
    "0": "Off",
    "1": "Master",
    "2": "Master 2",
    "3": "Top Master 1",
    "4": "Top Master 2",
    "5": "Mid Master 1",
    "6": "Mid Master 2",
    "7": "Client",
    "9": "Standalone Master"
  })

  const [PSTypeTranslationTable] = useState<{ [key: string]: string }>({
    "0": "Constant",
    "1": "Grid meter M",
    "2": "Grid meter IP"
  })

  async function fetchCustomCommands() {
    try {
      const response = await get(child(ref(getDatabase(fbConf)), 'voltie/projectpreferences/frontend/customCommands'))
      setCustomCommandOptions(response.val())
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
  }

  async function addCustomCommand() {
    if (customCommandEditorNameInput && customCommandEditorCommandInput) {

      // Delete command before adding the new if its an edit
      if (isEditingCustomCommand) {
        deleteCustomCommand(isEditingCustomCommand)
        setIsEditingCustomCommand(null)
      }

      if (/[.#$\[\]]/.test(customCommandEditorNameInput)) {
        alert(`Name can't contain ".", "#", "$", "[", or "]"`)
        return
      }

      try {
        JSON.parse(customCommandEditorCommandInput)
      } catch {
        alert(`Invalid JSON`)
        return
      }

      try {
        const command = JSON.parse(customCommandEditorCommandInput)
        try {
          await set(ref(getDatabase(fbConf), `voltie/projectpreferences/frontend/customCommands/${encodeURIComponent(customCommandEditorNameInput)}`), { command, description: customCommandEditorDescriptionInput })
        } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
        setTimeout(() => {
          fetchCustomCommands()
          setCustomCommandEditorNameInput("")
          setCustomCommandEditorCommandInput("")
          setCustomCommandEditorDescriptionInput("")
        }, 1000)
      } catch (err) {
        alert(`Firebase error, try logging out and in again`)
      }
    }
  }

  async function deleteCustomCommand(name: string) {
    try {
      await set(ref(getDatabase(fbConf), `voltie/projectpreferences/frontend/customCommands/${name}`), null)
    } catch (err) { addNotification(`Unexpected response: ${err}`, "TEXT", "error") }
    setTimeout(() => {
      fetchCustomCommands()
    }, 1000)
  }

  async function editCustomCommand(name: string) {
    if (customCommandOptions) {
      setIsEditingCustomCommand(name)
      setCustomCommandEditorNameInput(decodeURIComponent(name))
      setCustomCommandEditorCommandInput(JSON.stringify({ msg_type: customCommandOptions[name].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[name].command).filter(([k]) => k !== 'msg_type')) }))
      setCustomCommandEditorDescriptionInput(customCommandOptions[name].description)
    }
  }

  async function duplicateCustomCommand(name: string) {
    if (customCommandOptions) {
      setCustomCommandEditorNameInput(decodeURIComponent(name))
      setCustomCommandEditorCommandInput(JSON.stringify({ msg_type: customCommandOptions[name].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[name].command).filter(([k]) => k !== 'msg_type')) }))
      setCustomCommandEditorDescriptionInput(customCommandOptions[name].description)
    }
  }

  async function handleSendCustomCommand() {
    if (customCommandInputValue != '') {
      let command = ''
      try {
        command = JSON.parse(customCommandInputValue.replace(/[“”„‟]/g, '"'))
      } catch (err) {
        addNotification('Invalid JSON', "TEXT", "error")
      }

      if (command != '') {
        const response = await Comm('/api-websocket/mobile', {
          method: "POST",
          headers: {
            "Charger_id": charger.chargerId,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(command)
        })
        if (response.ok) {
          addNotification(JSON.stringify(await response.json()), "JSON")
        } else {
          addNotification(await response.text(), "TEXT", "error")
        }
      }

    } else {
      addNotification('Cant be empty', "TEXT", "warning")
    }
  }

  async function fetchCMD_GET_POWER_STAT() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 21 })
    })
    const result = await response.json()
    console.log(21, result)
    return result.power_stat || null
  }

  async function fetchCMD_GET_CONFIG() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 20 })
    })
    const result = await response.json()
    console.log(20, result)
    return result
  }

  async function fetchCMD_GET_RSP_STAT() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 22 })
    })
    const result = await response.json()
    console.log(22, result)
    return result
  }

  async function fetchCMD_PS_GET_RULES() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 59 })
    })
    const result = await response.json()
    console.log(59, result)
    return result
  }

  async function fetchCMD_GET_DIAG_CONF() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 110 })
    })
    const result = await response.json()
    console.log(110, result)
    setDiagnosticLogConfig(result.dlt_recs)
  }

  async function fetchCMD_STATUS_REQ() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 3 })
    })
    const result = await response.json()
    console.log(3, result)
    return result
  }

  async function fetchValues() {
    try {
      const power_stat = await fetchCMD_GET_POWER_STAT()
      const config = await fetchCMD_GET_CONFIG()
      const rsp_stat = await fetchCMD_GET_RSP_STAT()
      const status = await fetchCMD_STATUS_REQ()
      const ps_conf = (await fetchCMD_PS_GET_RULES()).ps_conf
      setChargerValues({ power_stat, config, rsp_stat, ps_conf, status })
      setIsChargerBlocked(await fetchIsChargerBlocked())
    } catch (e) { console.log(e, 'Charger probably offline') }
  }

  useEffect(() => {
    fetchValues()
  }, [])

  async function fetchCMD_GET_DEBUG_LEVEL() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 77 })
    })
    const result = await response.json()

    const resultObject: { name: string, value: number }[] = []
    for (let i = 0; i < result.module_names.length; i++) {
      resultObject.push({ name: result.module_names[i], value: result.debug_levels[i] })
    }

    setDebugLevels(resultObject)
  }

  async function fetchIsChargerBlocked() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 3 })
    })
    if (response.ok) {
      const result = await response.json()
      return result.evse_state == 18
    }
  }

  // TODO: This is a very stupid solution but I could not come up with a better one at the time
  function updateValueInChargerValues(keys: string, value: string | number | boolean) {
    if (chargerValues) {
      const newChargerValues = structuredClone(chargerValues)
      const path = keys.split('.')
      switch (path.length) {
        case 2:
          // @ts-expect-error: -
          newChargerValues[path[0]][path[1]] = value
          break;
        case 3:
          // @ts-expect-error: -
          newChargerValues[path[0]][path[1]][path[2]] = value
          break;
        case 4:
          // @ts-expect-error: -
          newChargerValues[path[0]][path[1]][path[2]][path[3]] = value
          break;
        case 5:
          // @ts-expect-error: -
          newChargerValues[path[0]][path[1]][path[2]][path[3]][path[4]] = value
          break;
      }
      setChargerValues(newChargerValues)
    }
  }

  async function handleSaveDisplayChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "display": chargerValues.config.display })
      })
      setIsShowingDisplaySettings(false)
    }
  }

  async function handleSaveLightChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "front_led": chargerValues.config.front_led, "rear_led": chargerValues.config.rear_led })
      })
      setIsShowingLightSettings(false)
    }
  }

  async function handleSaveSpeakerChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "sound": chargerValues.config.sound })
      })
      setIsShowingSpeakerSettings(false)
    }
  }

  async function handleSaveMidMeteringChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "power": chargerValues.config.power })
      })
      setIsShowingMidMetering(false)

      // Get the new values
      const config = await fetchCMD_GET_CONFIG()
      setChargerValues({ power_stat: chargerValues.power_stat, config, rsp_stat: chargerValues.rsp_stat, ps_conf: chargerValues.ps_conf, status: chargerValues.status })
    }
  }

  async function handleSavePowerOptionChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "power": chargerValues.config.power })
      })
      setIsShowingPowerOptions(false)
    }
  }

  async function handleSaveIpMeterSettingsChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "ip_485": chargerValues.config.ip_485 })
      })
      setIsShowingIpMeterSettings(false)
    }
  }

  async function handleSavePowerShareSettingsChanges() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 19, "pi2pi_netw": chargerValues.config.pi2pi_netw, "power": chargerValues.config.power })
      })

      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 58, "ps_conf": chargerValues.ps_conf })
      })

      setIsShowingPowerShareSettings(false)
    }
  }

  async function handleSaveDiagnosticLogConfig() {
    if (chargerValues) {
      await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 111, "dlt_recs": diagnosticLogConfig })
      })
      setIsShowingDiagnosticLogConfig(false)
    }
  }

  function secondsToTimeString(seconds: number) {
    const hours = Math.floor(seconds / 3600)
    const minutes = (seconds - (hours * 3600)) / 60
    return `${('0' + hours).slice(-2)}:${('0' + minutes).slice(-2)}`
  }

  function timeStringToSeconds(time: string) {
    const hours = time.split(':')[0]
    const minutes = time.split(':')[1]
    return (parseInt(hours) * 3600) + (parseInt(minutes) * 60)
  }

  function rgbToHex(r: number, g: number, b: number) {
    const componentToHex = (c: number) => {
      const hex = c.toString(16)
      return hex.length === 1 ? "0" + hex : hex
    }
    return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b)
  }

  function hexToRgb(hex: string) {
    hex = hex.replace(/^#/, '')
    const bigint = parseInt(hex, 16)
    const r = (bigint >> 16) & 255
    const g = (bigint >> 8) & 255
    const b = bigint & 255
    return { r, g, b }
  }

  async function handleSendBottomRowCommand(command: string, isConfirm: boolean) {

    if (isConfirm) {
      const confirmed = await confirm({ message: `${t('charger_areYouSureAboutSending')}: ${t(`charger_${command}`)}`, showCancel: true })
      if (!confirmed) return
    }

    const commandToSend = (() => {
      switch (command) {
        case 'logUpload': return { msg_type: 15, sub_cmd: 3 }
        case 'calibrateEvse': return { msg_type: 15, sub_cmd: 8 }
        case 'getProductInfo': return { msg_type: 12 }
        case 'getConfig': return { msg_type: 20 }
        case 'hardReboot': return { msg_type: 15, sub_cmd: 10 }
        case 'softReset': return { msg_type: 15, sub_cmd: 12 }
        case 'deleteCDRs': deleteCDRs(); return
        // Not a bottom row command but does the same
        case 'forceUpdate': return { msg_type: 15, sub_cmd: 1 }
      }
    })()

    if (commandToSend) {
      const response = await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(commandToSend)
      })
      if (response.ok) {
        addNotification(JSON.stringify(await response.json()), "JSON")
      } else {
        addNotification(await response.text(), "TEXT", "error")
      }
    }
  }

  async function deleteCDRs() {
    const response = await Comm('/api-cdr/cdrs/delete', {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify([charger.chargerId])
    })
    if (response.ok) {
      addNotification(JSON.stringify(await response.json()), "JSON")
    } else {
      addNotification(await response.text(), "TEXT", "error")
    }
  }

  async function toggleBlockCharger(isAvailable: boolean) {
    await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 62, "available": isAvailable })
    })
  }

  async function handleUpdateDebugLevel(module: number, level: number) {
    // Prevent setting the module to its current value
    if (debugLevels && debugLevels[module].value != level) {

      const response = await Comm('/api-websocket/mobile', {
        method: "POST",
        headers: {
          "Charger_id": charger.chargerId,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ "msg_type": 76, "module": module, "level": level })
      })

      debugLevels[module].value = level
      setDebugLevels(debugLevels)

      if (!response.ok) {
        addNotification("Something went wrong while setting debug level, charger might be offline", "TEXT", "error")
      }
    }
  }

  async function handleResetDebugLevelsToDefault() {
    const response = await Comm('/api-websocket/mobile', {
      method: "POST",
      headers: {
        "Charger_id": charger.chargerId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ "msg_type": 76, "reset": true })
    })


    if (!response.ok) {
      addNotification("Something went wrong while setting debug level, charger might be offline", "TEXT", "error")
    }

    fetchCMD_GET_DEBUG_LEVEL()
  }

  // Fetch power stats every 5 seconds if the detailed stats modal is open
  useEffect(() => {
    const powerStatsRefresh = setTimeout(async () => {
      if (isShowingDetailedStats && chargerValues) {
        const power_stat = await fetchCMD_GET_POWER_STAT()
        if (power_stat) setChargerValues({ ...chargerValues, power_stat })
      }
    }, 5000)

    return () => clearTimeout(powerStatsRefresh)
  }, [chargerValues, isShowingDetailedStats])

  return (
    <>
      {isCustomCommandsCommandEditorOpen && (
        <Modal onEscape={() => setIsCustomCommandsCommandEditorOpen(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsCustomCommandsCommandEditorOpen(false)} />
            </ModalCloseButtonContainer>
            {customCommandOptions ? <>
              <div style={{ width: 'min(max(75vw, 960px), calc(100vw - 72px))' }} />
              <Table id="CustomCommands" height="calc(85vh - 170px)" isSearchEnabled={true} isPaginationEnabled={false} triggerRefresh={[...JSON.stringify(customCommandOptions).split('')].reduce((acc, c) => acc + c.charCodeAt(0), 0)} isAutofocusOnSearchInput={true} columns={[
                { width: '15px' },
                { label: "Name", width: '15vw' },
                { label: "Command", width: '45vw' },
                { label: "Decription", width: '15vw' },
                { width: '15px' },
              ]} data={Object.keys(customCommandOptions).sort().map(command => [
                {}, {
                  onClick: () => { setCustomCommandInputValue(JSON.stringify({ msg_type: customCommandOptions[command].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[command].command).filter(([k]) => k !== 'msg_type')) })); setIsCustomCommandsCommandEditorOpen(false) },
                  sortBy: decodeURIComponent(command),
                  value: {
                    type: TableCellTypes.Label,
                    value: decodeURIComponent(command).slice(0, 35) + (decodeURIComponent(command).length > 35 ? '...' : '')
                  }
                }, {
                  onClick: () => (() => { setCustomCommandInputValue(JSON.stringify({ msg_type: customCommandOptions[command].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[command].command).filter(([k]) => k !== 'msg_type')) })); setIsCustomCommandsCommandEditorOpen(false) })(),
                  sortBy: (j => j.replace(/:/g, ': ').replace(/,/g, ', ').slice(0, 120) + (j.length > 120 ? '...' : ''))(JSON.stringify({ msg_type: customCommandOptions[command].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[command].command).filter(([k]) => k !== 'msg_type')) })),
                  value: {
                    type: TableCellTypes.Label,
                    value: (j => j.replace(/:/g, ': ').replace(/,/g, ', ').slice(0, 120) + (j.length > 120 ? '...' : ''))(JSON.stringify({ msg_type: customCommandOptions[command].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[command].command).filter(([k]) => k !== 'msg_type')) }))
                  }
                }, {
                  onClick: () => (() => { setCustomCommandInputValue(JSON.stringify({ msg_type: customCommandOptions[command].command.msg_type, ...Object.fromEntries(Object.entries(customCommandOptions[command].command).filter(([k]) => k !== 'msg_type')) })); setIsCustomCommandsCommandEditorOpen(false) })(),
                  sortBy: customCommandOptions[command].description,
                  value: {
                    type: TableCellTypes.Label,
                    value: customCommandOptions[command].description.slice(0, 40) + (customCommandOptions[command].description.length > 40 ? '...' : '')
                  }
                }, {
                  custom: <Dropdown handleChoose={(value) => value == t('charger_delete') ? deleteCustomCommand(command) : value == t('charger_duplicate') ? duplicateCustomCommand(command) : editCustomCommand(command)} title={{ icon: "general.more", label: '' }} options={[{ icon: "general.edit", label: t('charger_edit') }, { icon: "general.copy", label: t('charger_duplicate') }, { icon: "general.trash", label: t('charger_delete'), color: '#FD4E4E' }]} showOptionContainerOnLeft />
                }
              ])} />
              <WhiteSpace height="32px" />
              <DividerLineColor />
              <WhiteSpace height="32px" />
              <div style={{ display: 'flex' }}>
                <InputWithLabel label={"Name"} width={"33%"} value={customCommandEditorNameInput} onInput={value => setCustomCommandEditorNameInput(value)} />
                <WhiteSpace width="8px" />
                <InputWithLabel label={"Command"} width={"33%"} value={customCommandEditorCommandInput} onInput={value => setCustomCommandEditorCommandInput(value)} />
                <WhiteSpace width="8px" />
                <InputWithLabel label={"Description"} width={"33%"} value={customCommandEditorDescriptionInput} onInput={value => setCustomCommandEditorDescriptionInput(value)} />
                <WhiteSpace width="8px" />
                <Button label={isEditingCustomCommand ? t('charger_edit') : t('charger_add')} width={"10%"} onclick={addCustomCommand} />
              </div>
            </> : (
              <div style={{ width: "min(max(45vw, 576px), calc(100vw - 72px))" }}>
                <LoaderDots />
              </div>
            )}
          </ModalContainer>
        </Modal>
      )}
      <WhiteSpace height={'12px'} />
      <PageTitle label={t('charger_settings')} />
      <Container>
        <SettingsGrid>
          <tbody>
            <tr>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="dark" onClick={() => setIsShowingDetailedStats(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.powerDetails" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel $white>{t('charger_powerDetails')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" color="white" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="gray_200" onClick={() => setIsShowingDisplaySettings(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.display" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_display')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="warning" onClick={() => setIsShowingLightSettings(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.lights" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_lights')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="primary" onClick={() => setIsShowingSpeakerSettings(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.speakerOptions" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_speakerOptions')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="dark" onClick={() => setIsShowingPowerOptions(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.powerOptions" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel $white>{t('charger_powerOptions')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" color="white" />
                  </SettingArrow>
                </SettingContainer>
              </td>
            </tr>
            <tr>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="gray_200" onClick={() => setIsShowingConnections(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.connections" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_connections')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="dark" onClick={() => setIsShowingMidMetering(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.midMetering" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel $white>{t('charger_midMetering')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" color="white" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="gray_200" onClick={() => setIsShowingOcpp(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.ocppSettings" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_ocppSettings')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="gray_200" onClick={() => setIsShowingIntegrations(true)}>
                  <SettingIconContainer>
                    <Icon path="settings.gear" color="gray_900" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_integrations')}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                </SettingContainer>
              </td>
              <td style={{ width: '20%' }}>
                <SettingContainer $color="gray_300" style={{ cursor: 'auto' }}>
                  <SettingIconContainer>
                    <Icon path="chargers.chargerStatusBlocked" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{t('charger_chargerEnabled')}</SettingLabel>
                  <SettingSwitchSlot>
                    {isChargerBlocked != undefined ? <>
                      <Switch toggled={!isChargerBlocked} setToggled={(value) => { setIsChargerBlocked(!value); toggleBlockCharger(value) }} isBorder />
                    </> : <>
                      <p style={{ fontWeight: '700', fontSize: '14px', marginTop: "4px" }}><LoaderDots /></p>
                    </>}
                  </SettingSwitchSlot>
                </SettingContainer>
              </td>
            </tr>
            {hasPowerShareStatistics(chargerValues?.config?.pi2pi_netw) && (
              <tr>
                <td style={{ width: '20%' }}>
                  <SettingContainer $color="gray_200" onClick={() => setIsShowingPowerShareStatistics(true)}>
                    <SettingIconContainer>
                      <Icon path="settings.amper" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel>{t('charger_powerShareStatistics')}</SettingLabel>
                    <SettingArrow>
                      <Icon path="general.forwardArrow" width="100%" height="100%" />
                    </SettingArrow>
                  </SettingContainer>
                </td>
              </tr>
            )}
          </tbody>
        </SettingsGrid>
        {isAdmin && <>
          <WhiteSpace height={'32px'} />
          <DividerLineColor />
          <WhiteSpace height={'32px'} />
          <SettingsGrid>
            <tbody>
              <tr>
                <td style={{ width: '20%' }}>
                  <SettingContainer $color="white" onClick={() => handleSendBottomRowCommand('forceUpdate', true)}>
                    <SettingIconContainer>
                      <Icon path="general.refresh" color="gray_900" width="70%" height="70%" marginleft="15%" margintop="15%" />
                    </SettingIconContainer>
                    <SettingLabel>{t('charger_forceUpdate')}</SettingLabel>
                    <SettingArrow>
                      <Icon path="general.forwardArrow" width="100%" height="100%" />
                    </SettingArrow>
                  </SettingContainer>
                </td>
                <td style={{ width: '20%' }}>
                  <SettingContainer $color="white" onClick={() => { setIsShowingDebugLevels(true); fetchCMD_GET_DEBUG_LEVEL() }}>
                    <SettingIconContainer>
                      <Icon path="general.wrench" color="gray_900" width="70%" height="70%" marginleft="15%" margintop="15%" />
                    </SettingIconContainer>
                    <SettingLabel>{t('charger_debugLevels')}</SettingLabel>
                    <SettingArrow>
                      <Icon path="general.forwardArrow" width="100%" height="100%" />
                    </SettingArrow>
                  </SettingContainer>
                </td>
                <td style={{ width: '20%' }}>
                  <SettingContainer $color="white" onClick={() => setIsShowingIpMeterSettings(true)}>
                    <SettingIconContainer>
                      <Icon path="settings.ethernet" color="gray_900" width="70%" height="70%" marginleft="15%" margintop="15%" />
                    </SettingIconContainer>
                    <SettingLabel>{t('charger_ipMeterSettings')}</SettingLabel>
                    <SettingArrow>
                      <Icon path="general.forwardArrow" width="100%" height="100%" />
                    </SettingArrow>
                  </SettingContainer>
                </td>
                <td style={{ width: '20%' }}>
                  <SettingContainer $color="white" onClick={() => setIsShowingPowerShareSettings(true)}>
                    <SettingIconContainer>
                      <Icon path="general.more" color="gray_900" width="70%" height="70%" marginleft="15%" margintop="15%" />
                    </SettingIconContainer>
                    <SettingLabel>{t('charger_powerShareSettings')}</SettingLabel>
                    <SettingArrow>
                      <Icon path="general.forwardArrow" width="100%" height="100%" />
                    </SettingArrow>
                  </SettingContainer>
                </td>
                <td style={{ width: '20%' }}>
                  <SettingContainer $color="white" onClick={() => { setIsShowingDiagnosticLogConfig(true); fetchCMD_GET_DIAG_CONF() }}>
                    <SettingIconContainer>
                      <Icon path="general.more" color="gray_900" width="70%" height="70%" marginleft="15%" margintop="15%" />
                    </SettingIconContainer>
                    <SettingLabel>{t('charger_diagnosticLogConfig')}</SettingLabel>
                    <SettingArrow>
                      <Icon path="general.forwardArrow" width="100%" height="100%" />
                    </SettingArrow>
                  </SettingContainer>
                </td>
              </tr>
            </tbody>
          </SettingsGrid>
          <WhiteSpace height={'32px'} />
          <div style={{ minWidth: 'min(max(30vw, 384px), calc(100vw - 32px))', display: 'inline-block' }}>
            <SettingContainer $noFlex $noAnimation $noCursorPointer $color="white">
              <CustomCommandsHeaderRow>
                <CustomCommandsIconBackground>
                  <SettingIconContainer>
                    <Icon path="settings.gear" color="gray_300" width="60%" height="60%" marginleft="20%" margintop="20%" />
                  </SettingIconContainer>
                </CustomCommandsIconBackground>
                <SettingLabel>{t('charger_customCommands')}</SettingLabel>
                <WhiteSpace width='48px' />
                {/*<div style={{ position: 'absolute', right: '48px', width: '40%', zIndex: '100' }}>
                  <Select width={'100%'} label={t('charger_command')} isAbsolute options={Object.keys(customCommandDropdownOptions)} handleSelect={(value) => setCustomCommandInputValue(JSON.stringify(customCommandDropdownOptions[value]))} />
                </div>*/}
                <CustomCommandActionSlot>
                  <ButtonWithIcon label={t('charger_selectCommand')} onClick={() => { fetchCustomCommands(); setIsCustomCommandsCommandEditorOpen(true) }} icon="general.wrench" iconColor="background" backgroundColor="gray_900" />
                </CustomCommandActionSlot>
              </CustomCommandsHeaderRow>
              <WhiteSpace height='24px' />
              <CustomCommandsTextInput value={customCommandInputValue != '{}' ? customCommandInputValue : ''} onInput={(e) => setCustomCommandInputValue((e.target as HTMLInputElement).value)} />
              <WhiteSpace height='24px' />
              <div style={{ display: 'inline-block', width: '100%' }}>
                <div style={{ display: 'flex' }}>
                  <div style={{ width: '100%', display: 'flex' }}>
                    <Navigator to='https://voltie.atlassian.net/wiki/spaces/VOLTIE/pages/392003585/Frontendr+l+kiadhat+parancs+mint+k' newTab>
                      <ButtonWithIcon label={t('charger_commandSamples')} onClick={() => { }} icon="general.externalLink" iconColor="background" backgroundColor="gray_900" />
                    </Navigator>
                  </div>
                  <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                    <ButtonWithIcon label={t('charger_send')} onClick={handleSendCustomCommand} />
                  </div>
                </div>
              </div>
            </SettingContainer>
          </div>
          <WhiteSpace height={'32px'} />
          <DividerLineGray />
          <SettingsGrid>
            <tbody>
              <tr>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('logUpload', false)}>
                    <SettingIconContainer $small>
                      <Icon path="general.log" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_logUpload')}</SettingLabel>
                  </SettingContainer>
                </td>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('calibrateEvse', true)}>
                    <SettingIconContainer $small>
                      <Icon path="settings.calibrateEvse" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_calibrateEvse')}</SettingLabel>
                  </SettingContainer>
                </td>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('getProductInfo', false)}>
                    <SettingIconContainer $small>
                      <Icon path="settings.gear" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_getProductInfo')}</SettingLabel>
                  </SettingContainer>
                </td>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('getConfig', true)}>
                    <SettingIconContainer $small>
                      <Icon path="general.wrench" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_getConfig')}</SettingLabel>
                  </SettingContainer>
                </td>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('hardReboot', true)}>
                    <SettingIconContainer $small>
                      <Icon path="general.wrench" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_hardReboot')}</SettingLabel>
                  </SettingContainer>
                </td>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('softReset', true)}>
                    <SettingIconContainer $small>
                      <Icon path="general.refresh" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_softReset')}</SettingLabel>
                  </SettingContainer>
                </td>
                <td style={{ width: '14.2%' }}>
                  <SettingContainer onClick={() => handleSendBottomRowCommand('deleteCDRs', true)}>
                    <SettingIconContainer $small>
                      <Icon path="general.trash" color="gray_900" width="100%" height="100%" />
                    </SettingIconContainer>
                    <SettingLabel $small>{t('charger_deleteCDRs')}</SettingLabel>
                  </SettingContainer>
                </td>
              </tr>
            </tbody>
          </SettingsGrid>
          <DividerLineGray />
        </>}
      </Container>





      {isShowingDetailedStats && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingDetailedStats(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingDetailedStats(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_powerDetails')}</ModalTitle>
            <WhiteSpace height="34px" />
            <div style={{ overflowY: 'scroll', maxHeight: "calc(85dvh - 100px)" }}>
              {[
                { value1: chargerValues.power_stat.dlm_current1, value2: chargerValues.power_stat.dlm_current2, value3: chargerValues.power_stat.dlm_current3, unit: 'A', title: <><Icon path="settings.amper" color="primary" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitleThin>/</ModalDetailTitleThin><Icon path="settings.home" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitle>{t('charger_amperPhase')}</ModalDetailTitle></> },
                { value1: chargerValues.power_stat.current1, value2: chargerValues.power_stat.current2, value3: chargerValues.power_stat.current3, unit: 'A', title: <><Icon path="settings.amper" color="primary" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitleThin>/</ModalDetailTitleThin><Icon path="general.charger" width="16px" height="16px" marginleft="8px" marginright="8px" margintop="4px" /><ModalDetailTitle>{t('charger_amperPhase')}</ModalDetailTitle></> },
                { value1: chargerValues.power_stat.power1, value2: chargerValues.power_stat.power2, value3: chargerValues.power_stat.power3, unit: 'kW', title: <><Icon path="chargers.power" color="warning" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitle>{t('charger_powerPhase')}</ModalDetailTitle></> },
                { value1: +chargerValues.power_stat.voltage1.toFixed(1), value2: +chargerValues.power_stat.voltage2.toFixed(1), value3: +chargerValues.power_stat.voltage3.toFixed(1), unit: 'V', title: <><Icon path="settings.voltage" color="warning" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitle>{t('charger_voltagePhase')}</ModalDetailTitle></> },
                isAdmin ? { value1: chargerValues.power_stat.temp_pow, value2: chargerValues.power_stat.temp_uc, value3: chargerValues.power_stat.temp_pi, unit: '°C', title: <><Icon path="settings.thermometer" color="danger" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitle>{t('charger_temperature')}</ModalDetailTitle><WhiteSpace width='8px' /><ModalDetailTitleThin>PW / UC / PI</ModalDetailTitleThin></> } : null,
                isAdmin ? { value1: chargerValues.power_stat.hum, unit: '%', title: <><Icon path="settings.humidity" color="primary" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitle>{t('charger_humidity')}</ModalDetailTitle></> } : null,
                chargerValues.power_stat.ipm_valid ? { value1: chargerValues.power_stat.ipm_current1.toFixed(1), value2: chargerValues.power_stat.ipm_current2.toFixed(1), value3: chargerValues.power_stat.ipm_current3.toFixed(1), unit: 'A', title: <><Icon path="settings.amper" color="primary" width="24px" height="24px" marginleft="4px" marginright="4px" /><ModalDetailTitle>{t('charger_ipMeterPhase')}</ModalDetailTitle></> } : null
              ].map(detail => detail != null && (
                <>
                  <div style={{ display: 'flex' }}>{detail.title}</div>
                  <WhiteSpace height="8px" />
                  <PhaseValueRow>
                    <span>{detail.value1}<DetailedStatsValueUnit>{detail.unit}</DetailedStatsValueUnit></span>
                    {detail.value2 != null && <span>{detail.value2}<DetailedStatsValueUnit>{detail.unit}</DetailedStatsValueUnit></span>}
                    {detail.value3 != null && <span>{detail.value3}<DetailedStatsValueUnit>{detail.unit}</DetailedStatsValueUnit></span>}
                  </PhaseValueRow>
                  <ModalDetailDivider />
                </>
              ))}
            </div>
            <ModalButtonContainer>
              <ModalButton onClick={async () => { const power_stat = await fetchCMD_GET_POWER_STAT(); if (power_stat) setChargerValues({ ...chargerValues, power_stat }) }} $hasBg>{t('charger_refresh')}</ModalButton>
              <ModalButton onClick={() => setIsShowingDetailedStats(false)}>{t('charger_close')}</ModalButton>
            </ModalButtonContainer>
          </ModalContainer>
        </Modal>
      )}





      {isShowingDisplaySettings && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingDisplaySettings(false)}>
          <ModalContainer style={{ position: 'relative', overflowY: 'scroll', overflowX: 'hidden' }}>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingDisplaySettings(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_displaySettings')}</ModalTitle>
            <WhiteSpace height="34px" />
            <Select
              handleSelect={(value) => updateValueInChargerValues('config.display.language', DISPLAY_LANGUAGES.find(l => l.label === value)?.id ?? 0)}
              width="calc(100% - 20px)"
              icon={DISPLAY_LANGUAGES.find(l => l.id === chargerValues.config.display.language)?.icon}
              label={t('charger_language')}
              options={[DISPLAY_LANGUAGES.find(l => l.id === chargerValues.config.display.language)?.label ?? DISPLAY_LANGUAGE_LABELS[0], ...DISPLAY_LANGUAGE_LABELS]} isAbsolute />
            <WhiteSpace height="28px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_displayEnabled')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.display.enabled} setToggled={(value) => updateValueInChargerValues('config.display.enabled', value)} />
              </div>
            </div>
            <WhiteSpace height="8px" />
            <ModalDetailDivider />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_eyesEnabled')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.display.eyes_en} setToggled={(value) => updateValueInChargerValues('config.display.eyes_en', value)} />
              </div>
            </div>
            <WhiteSpace height="8px" />
            <ModalDetailDivider />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_nightmodeEnabled')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.display.night_mode_en} setToggled={(value) => updateValueInChargerValues('config.display.night_mode_en', value)} />
              </div>
            </div>
            <WhiteSpace height="8px" />

            <ModalDetailDivider $isColor />

            <WhiteSpace height="8px" />
            <ModalDetailTitle>{t('charger_textScrollingSpeed')}</ModalDetailTitle>
            <WhiteSpace height="18px" />
            <Slider value={chargerValues.config.display.scroll_speed} setValue={(value) => updateValueInChargerValues('config.display.scroll_speed', value)} min={1} max={100} step={1} numberOfDots={5} width="100%" firstLabel={t('charger_slow')} lastLabel={t('charger_fast')} />
            <WhiteSpace height="24px" />
            <ModalDetailDivider />
            <WhiteSpace height="8px" />
            <ModalDetailTitle>{t('charger_brightness')}</ModalDetailTitle>
            <WhiteSpace height="18px" />
            <Slider value={chargerValues.config.display.brightness} setValue={(value) => updateValueInChargerValues('config.display.brightness', value)} min={10} max={100} step={1} numberOfDots={5} width="100%" firstImg="settings.dim" lastImg="settings.bright" firstImgColor="gray_500" lastImgColor="gray_500" />
            <WhiteSpace height="24px" />
            <ModalDetailDivider />
            <WhiteSpace height="8px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_displayStaysActive')}</ModalDetailTitle>
              <WhiteSpace width="4px" />
              <ModalDetailTitleThin>{`(${t('charger_minute')})`}</ModalDetailTitleThin>
            </div>
            <WhiteSpace height="18px" />
            <Slider value={chargerValues.config.display.on_seconds == 0 ? 600 : chargerValues.config.display.on_seconds} setValue={(value) => updateValueInChargerValues('config.display.on_seconds', value == 600 ? 0 : value)} min={60} max={600} step={1} numberOfDots={11} width="100%" dotLabels={['1', '2', '3', '4', '5', '6', '7', '8', '9', '∞']} />

            <WhiteSpace height="24px" />
            <ModalDetailDivider />
            <WhiteSpace height="8px" />
            <ModalDetailTitle>{t('charger_idleStateText')}</ModalDetailTitle>
            <WhiteSpace height="18px" />
            <div style={{ display: 'flex' }}>
              <InputWithLabel label={t('charger_idleText')} width={"69%"} value={chargerValues.config.display.idle_text} onInput={value => updateValueInChargerValues('config.display.idle_text', value)} />
              <WhiteSpace width="1%" />
              <InputWithLabel label={t('charger_pauseBetweenTextsSec')} width={"30%"} value={chargerValues.config.display.idle_text_interval.toString()} onInput={value => updateValueInChargerValues('config.display.idle_text_interval', parseInt(value) || 0)} />
            </div>
            <WhiteSpace height="64px" />


          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSaveDisplayChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingDisplaySettings(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}





      {isShowingLightSettings && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingLightSettings(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingLightSettings(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_lightSettings')}</ModalTitle>
            <WhiteSpace height="34px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_frontLight')}</ModalDetailTitle>
              <WhiteSpace width='8px' />
              <ModalDetailTitleThin>{`(${t('charger_mouth')})`}</ModalDetailTitleThin>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.front_led.enabled} setToggled={(value) => updateValueInChargerValues('config.front_led.enabled', value)} />
              </div>
            </div>
            <WhiteSpace height="42px" />
            <Slider value={chargerValues.config.front_led.brightness} setValue={(value) => updateValueInChargerValues('config.front_led.brightness', value)} min={10} max={100} step={1} numberOfDots={5} width="100%" firstImg="settings.dim" lastImg="settings.bright" firstImgColor="gray_500" lastImgColor="gray_500" />
            <WhiteSpace height="32px" />
            <ModalDetailDivider />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_turnedOffPeriod')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.front_led.night_mode_en} setToggled={(value) => updateValueInChargerValues('config.front_led.night_mode_en', value)} />
              </div>
            </div>
            {chargerValues.config.front_led.night_mode_en && (
              <>
                <WhiteSpace height="18px" />
                <ModalDetailTitle>{t('charger_timeIntervall')}</ModalDetailTitle>
                <WhiteSpace height="8px" />
                <TimeInput type="time" value={secondsToTimeString(chargerValues.config.front_led.night_period_st)} onChange={(e) => updateValueInChargerValues('config.front_led.night_period_st', timeStringToSeconds(e.target.value))} />
                <TimeInput $right type="time" value={secondsToTimeString(chargerValues.config.front_led.night_period_sp)} onChange={(e) => updateValueInChargerValues('config.front_led.night_period_sp', timeStringToSeconds(e.target.value))} />
              </>
            )}
            <WhiteSpace height="16px" />

            <ModalDetailDivider $isColor />

            <WhiteSpace height="8px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_backLight')}</ModalDetailTitle>
              <WhiteSpace width='8px' />
              <ModalDetailTitleThin>{`(${t('charger_backpanel')})`}</ModalDetailTitleThin>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.rear_led.enabled} setToggled={(value) => updateValueInChargerValues('config.rear_led.enabled', value)} />
              </div>
            </div>
            <WhiteSpace height="42px" />
            <Slider value={chargerValues.config.rear_led.brightness} setValue={(value) => updateValueInChargerValues('config.rear_led.brightness', value)} min={10} max={100} step={1} numberOfDots={5} width="100%" firstImg="settings.dim" lastImg="settings.bright" firstImgColor="gray_500" lastImgColor="gray_500" />
            <WhiteSpace height="32px" />
            <ModalDetailDivider />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_color')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <ColorInput type="color"
                  value={rgbToHex(chargerValues.config.rear_led.color_r, chargerValues.config.rear_led.color_g, chargerValues.config.rear_led.color_b)}
                  onChange={(e) => {
                    const rgb = hexToRgb(e.target.value)
                    const newChargerValues: ChargerValuesType = structuredClone(chargerValues)
                    newChargerValues.config.rear_led.color_r = rgb.r
                    newChargerValues.config.rear_led.color_g = rgb.g
                    newChargerValues.config.rear_led.color_b = rgb.b
                    setChargerValues(newChargerValues)
                  }}
                />
              </div>
            </div>
            <WhiteSpace height="8px" />
            <ModalDetailDivider />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_turnedOffPeriod')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.rear_led.night_mode_en} setToggled={(value) => updateValueInChargerValues('config.rear_led.night_mode_en', value)} />
              </div>
            </div>
            {chargerValues.config.rear_led.night_mode_en && (
              <>
                <WhiteSpace height="18px" />
                <ModalDetailTitle>{t('charger_timeIntervall')}</ModalDetailTitle>
                <WhiteSpace height="8px" />
                <TimeInput type="time" value={secondsToTimeString(chargerValues.config.rear_led.night_period_st)} onChange={(e) => updateValueInChargerValues('config.rear_led.night_period_st', timeStringToSeconds(e.target.value))} />
                <TimeInput $right type="time" value={secondsToTimeString(chargerValues.config.rear_led.night_period_sp)} onChange={(e) => updateValueInChargerValues('config.rear_led.night_period_sp', timeStringToSeconds(e.target.value))} />
              </>
            )}
          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSaveLightChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingLightSettings(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}





      {isShowingSpeakerSettings && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingSpeakerSettings(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingSpeakerSettings(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_speakerSettings')}</ModalTitle>
            <WhiteSpace height="34px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_speakerState')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.sound.enabled} setToggled={(value) => updateValueInChargerValues('config.sound.enabled', value)} />
              </div>
            </div>
            <WhiteSpace height="8px" />
            <ModalDetailDivider />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_turnedOffPeriod')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.sound.night_mode_en} setToggled={(value) => updateValueInChargerValues('config.sound.night_mode_en', value)} />
              </div>
            </div>
            {chargerValues.config.sound.night_mode_en && (
              <>
                <WhiteSpace height="18px" />
                <ModalDetailTitle>{t('charger_timeIntervall')}</ModalDetailTitle>
                <WhiteSpace height="8px" />
                <TimeInput type="time" value={secondsToTimeString(chargerValues.config.sound.night_period_st)} onChange={(e) => updateValueInChargerValues('config.sound.night_period_st', timeStringToSeconds(e.target.value))} />
                <TimeInput $right type="time" value={secondsToTimeString(chargerValues.config.sound.night_period_sp)} onChange={(e) => updateValueInChargerValues('config.sound.night_period_sp', timeStringToSeconds(e.target.value))} />
              </>
            )}
          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSaveSpeakerChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingSpeakerSettings(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}





      {isShowingPowerOptions && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingPowerOptions(false)}>
          <ModalContainer style={{ position: 'relative', overflowY: 'scroll', overflowX: 'hidden' }}>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingPowerOptions(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_powerOptions')}</ModalTitle>
            <WhiteSpace height="34px" />


            <ModalDetailRow>
              <Icon path="chargers.average" width="24px" height="24px" marginleft="4px" marginright="4px" />
              <ModalDetailTitleThin>{`${t('charger_maxAmper')}:`}</ModalDetailTitleThin>
              <WhiteSpace width='8px' />
              <ModalDetailTitle>{`${chargerValues.config.power.curr_usr_limit} A`}</ModalDetailTitle>
              <ModalDetailTrailing>
                <ModalDetailTitleThin>{`${t('charger_maxPower')}:`}</ModalDetailTitleThin>
                <WhiteSpace width='8px' />
                <ModalDetailTitle>{`${chargerValues.config.power.curr_usr_limit * (230 * (chargerValues.status.phase || 3)) / 1000} kW`}</ModalDetailTitle>
              </ModalDetailTrailing>
            </ModalDetailRow>
            <WhiteSpace height="18px" />
            <Slider value={chargerValues.config.power.curr_usr_limit} setValue={(value) => updateValueInChargerValues('config.power.curr_usr_limit', value)} min={chargerValues.config.power.curr_hw_limit_min} max={chargerValues.config.power.curr_hw_limit} step={1} numberOfDots={5} width="100%" firstLabel={`Min. (${chargerValues.config.power.curr_hw_limit_min})`} lastLabel={`Max. (${chargerValues.config.power.curr_hw_limit})`} />
            <WhiteSpace height="32px" />

            <ModalDetailDivider $isColor />

            <WhiteSpace height="8px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_scheduledPower')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.power.night_mode_en} setToggled={(value) => updateValueInChargerValues('config.power.night_mode_en', value)} />
              </div>
            </div>
            {chargerValues.config.power.night_mode_en && (
              <>
                <WhiteSpace height='32px' />
                <div style={{ display: 'flex' }}>
                  <Icon path="chargers.average" width="24px" height="24px" marginleft="4px" marginright="4px" />
                  <ModalDetailTitleThin>{`${t('charger_maxAmper')}:`}</ModalDetailTitleThin>
                  <WhiteSpace width='8px' />
                  <ModalDetailTitle>{`${chargerValues.config.power.curr_night_limit} A`}</ModalDetailTitle>
                  <div style={{ display: 'flex', position: 'absolute', right: '20px' }}>
                    <ModalDetailTitleThin>{`${t('charger_maxPower')}:`}</ModalDetailTitleThin>
                    <WhiteSpace width='8px' />
                    <ModalDetailTitle>{`${chargerValues.config.power.curr_night_limit * (230 * (chargerValues.status.phase || 3)) / 1000} kW`}</ModalDetailTitle>
                  </div>
                </div>
                <WhiteSpace height="18px" />
                <Slider value={chargerValues.config.power.curr_night_limit} setValue={(value) => updateValueInChargerValues('config.power.curr_night_limit', value)} min={chargerValues.config.power.curr_hw_limit_min} max={chargerValues.config.power.curr_hw_limit} step={1} numberOfDots={5} width="100%" firstLabel={`Min. (${chargerValues.config.power.curr_hw_limit_min})`} lastLabel={`Max. (${chargerValues.config.power.curr_hw_limit})`} />
                <div>
                  <WhiteSpace height="48px" />
                  <ModalDetailTitle>{t('charger_timeIntervall')}</ModalDetailTitle>
                  <WhiteSpace height="8px" />
                  <TimeInput type="time" value={secondsToTimeString(chargerValues.config.power.night_period_st)} onChange={(e) => updateValueInChargerValues('config.power.night_period_st', timeStringToSeconds(e.target.value))} />
                  <TimeInput $right type="time" value={secondsToTimeString(chargerValues.config.power.night_period_sp)} onChange={(e) => updateValueInChargerValues('config.power.night_period_sp', timeStringToSeconds(e.target.value))} />
                </div>
              </>
            )}
            <WhiteSpace height="16px" />

            <ModalDetailDivider $isColor />

            <WhiteSpace height="8px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_intelligentPower')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.power.dlm_mode == 1 || chargerValues.config.power.dlm_mode == 2 || chargerValues.config.power.dlm_mode == 3} setToggled={(value) => {
                  if (chargerValues.config.power.dlm_meter_state != 0 && chargerValues.config.power.dlm_meter_state != 1) {
                    updateValueInChargerValues('config.power.dlm_mode', value ? 1 : 0)
                  }
                }} />
              </div>
            </div>
            {(chargerValues.config.power.dlm_meter_state == 0 || chargerValues.config.power.dlm_meter_state == 1) && (
              <>
                <WhiteSpace height='16px' />
                <div style={{ display: 'flex' }}>
                  <Icon path="general.warning" width="24px" height="24px" marginleft="4px" marginright="4px" />
                  <WhiteSpace width='4px' />
                  <ModalDetailTitleThin>{t('charger_externalMeterIsNotAvailable')}</ModalDetailTitleThin>
                </div>
              </>
            )}
            {(chargerValues.config.power.dlm_mode == 1 || chargerValues.config.power.dlm_mode == 2 || chargerValues.config.power.dlm_mode == 3) && (
              <>
                <div style={{ display: 'flex', marginTop: '18px', backgroundColor: '#E4E6EF', borderRadius: '12px', padding: '3px' }}>
                  <Button label={t('charger_same')} width="50%" bgcolor={chargerValues.config.power.dlm_dyn_specific ? "#E4E6EF" : "#F5F8FA"} labelcolor="#181C32" padding="8px" onclick={() => {
                    updateValueInChargerValues('config.power.dlm_dyn_specific', false)
                  }} />
                  <Button label={t('charger_specific')} width="50%" bgcolor={chargerValues.config.power.dlm_dyn_specific ? "#F5F8FA" : "#E4E6EF"} labelcolor="#181C32" padding="8px" onclick={() => {
                    updateValueInChargerValues('config.power.dlm_dyn_specific', true)
                  }} />
                </div>
                {!chargerValues.config.power.dlm_dyn_specific ? <>
                  <WhiteSpace height='32px' />
                  <div style={{ display: 'flex' }}>
                    <Icon path="chargers.average" width="24px" height="24px" marginleft="4px" marginright="4px" />
                    <ModalDetailTitleThin>{`${t('charger_maxAmper')}:`}</ModalDetailTitleThin>
                    <WhiteSpace width='8px' />
                    <ModalDetailTitle>{`${chargerValues.config.power.dlm_dyn_curr_lim} A`}</ModalDetailTitle>
                    <div style={{ display: 'flex', position: 'absolute', right: '20px' }}>
                      <ModalDetailTitleThin>{`${t('charger_maxPower')}:`}</ModalDetailTitleThin>
                      <WhiteSpace width='8px' />
                      <ModalDetailTitle>{`${chargerValues.config.power.dlm_dyn_curr_lim * (230 * (chargerValues.status.phase || 3)) / 1000} kW`}</ModalDetailTitle>
                    </div>
                  </div>
                  <WhiteSpace height="18px" />
                  {/* Max amper limit is not based on curr_hw_limit as before, but set manually to 150 */}
                  <Slider value={chargerValues.config.power.dlm_dyn_curr_lim} setValue={(value) => updateValueInChargerValues('config.power.dlm_dyn_curr_lim', value)} min={chargerValues.config.power.curr_hw_limit_min} max={/*chargerValues.config.power.curr_hw_limit*/ 150} step={1} numberOfDots={5} width="100%" firstLabel={`Min. (${chargerValues.config.power.curr_hw_limit_min})`} lastLabel={`Max. (${/*chargerValues.config.power.curr_hw_limit*/ 150})`} />
                </> : <>
                  <WhiteSpace height='32px' />
                  <div style={{ display: 'flex' }}>
                    <ModalDetailTitleThin>{`${t('charger_phase')} 1:`}</ModalDetailTitleThin>
                    <WhiteSpace width='8px' />
                    <ModalDetailTitle>{`${chargerValues.config.power.dlm_dyn_curr1_lim} A`}</ModalDetailTitle>
                  </div>
                  <WhiteSpace height="8px" />
                  <Slider value={chargerValues.config.power.dlm_dyn_curr1_lim} setValue={(value) => updateValueInChargerValues('config.power.dlm_dyn_curr1_lim', value)} min={chargerValues.config.power.curr_hw_limit_min} max={150} step={1} numberOfDots={5} width="100%" firstLabel={`Min. (${chargerValues.config.power.curr_hw_limit_min})`} lastLabel={`Max. (${150})`} />

                  <WhiteSpace height="42px" />

                  <div style={{ display: 'flex' }}>
                    <ModalDetailTitleThin>{`${t('charger_phase')} 2:`}</ModalDetailTitleThin>
                    <WhiteSpace width='8px' />
                    <ModalDetailTitle>{`${chargerValues.config.power.dlm_dyn_curr2_lim} A`}</ModalDetailTitle>
                  </div>
                  <WhiteSpace height="8px" />
                  <Slider value={chargerValues.config.power.dlm_dyn_curr2_lim} setValue={(value) => updateValueInChargerValues('config.power.dlm_dyn_curr2_lim', value)} min={chargerValues.config.power.curr_hw_limit_min} max={150} step={1} numberOfDots={5} width="100%" firstLabel={`Min. (${chargerValues.config.power.curr_hw_limit_min})`} lastLabel={`Max. (${150})`} />

                  <WhiteSpace height="42px" />

                  <div style={{ display: 'flex' }}>
                    <ModalDetailTitleThin>{`${t('charger_phase')} 3:`}</ModalDetailTitleThin>
                    <WhiteSpace width='8px' />
                    <ModalDetailTitle>{`${chargerValues.config.power.dlm_dyn_curr3_lim} A`}</ModalDetailTitle>
                  </div>
                  <WhiteSpace height="8px" />
                  <Slider value={chargerValues.config.power.dlm_dyn_curr3_lim} setValue={(value) => updateValueInChargerValues('config.power.dlm_dyn_curr3_lim', value)} min={chargerValues.config.power.curr_hw_limit_min} max={150} step={1} numberOfDots={5} width="100%" firstLabel={`Min. (${chargerValues.config.power.curr_hw_limit_min})`} lastLabel={`Max. (${150})`} />
                </>}
                <WhiteSpace height="16px" />
              </>
            )}
            <WhiteSpace height="16px" />
            {chargerValues.config.power.dlm_meter_state != 0 && (chargerValues.config.power.dlm_mode == 1 || chargerValues.config.power.dlm_mode == 2 || chargerValues.config.power.dlm_mode == 3) && (
              <>
                <ModalDetailDivider $isColor />

                <WhiteSpace height="8px" />
                <div style={{ display: 'flex' }}>
                  <ModalDetailTitle>{t('charger_solarPower')}</ModalDetailTitle>
                  <div style={{ position: 'absolute', right: '20px' }}>
                    <Switch toggled={chargerValues.config.power.dlm_mode == 2 || chargerValues.config.power.dlm_mode == 3} setToggled={(value) => updateValueInChargerValues('config.power.dlm_mode', value ? 2 : 1)} />
                  </div>
                </div>
                <WhiteSpace height="16px" />
                {(chargerValues.config.power.dlm_mode == 2 || chargerValues.config.power.dlm_mode == 3) && <>
                  <WhiteSpace height="12px" />
                  <div style={{ display: 'flex' }}>
                    <Icon path="settings.solarEcoMode" color="none" style={chargerValues.config.power.dlm_mode == 2 ? { width: '41%', cursor: 'pointer', position: 'absolute', left: '32px' } : { width: '41%', filter: 'grayscale(1)', cursor: 'pointer', position: 'absolute', left: '32px' }} onClick={() => updateValueInChargerValues('config.power.dlm_mode', 2)} />
                    <Icon path="settings.solarGreenMode" color="none" style={chargerValues.config.power.dlm_mode == 3 ? { width: '41%', cursor: 'pointer', position: 'absolute', right: '32px' } : { width: '41%', filter: 'grayscale(1)', cursor: 'pointer', position: 'absolute', right: '32px' }} onClick={() => updateValueInChargerValues('config.power.dlm_mode', 3)} />
                  </div>
                  <div style={{ height: 'calc(max(20vw, 256px) / 100 * 82 / 3)' }} />
                </>}
              </>
            )}


            {chargerValues.config.power.dlm_meter_state != 0 && <>

              <ModalDetailDivider $isColor />

              <WhiteSpace height="8px" />
              <div style={{ display: 'flex' }}>
                <ModalDetailTitle>{t('charger_solarProductionProtection')}</ModalDetailTitle>
                <div style={{ position: 'absolute', right: '20px' }}>
                  <Switch toggled={chargerValues.config.power.dlm_mode == 4} setToggled={value => updateValueInChargerValues('config.power.dlm_mode', value ? 4 : 0)} />
                </div>
              </div>

              {chargerValues.config.power.dlm_mode == 4 && <>
                <WhiteSpace height="22px" />
                <InputWithLabel label={t('charger_VoltageForStopCharging')} width={"max(19vw, 243.2px)"} value={chargerValues.config.power.dlm_grid_u_stop.toString()} onInput={value => updateValueInChargerValues('config.power.dlm_grid_u_stop', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={t('charger_VoltageForDecreaseChargingCurrent')} width={"max(19vw, 243.2px)"} value={chargerValues.config.power.dlm_grid_u_min.toString()} onInput={value => updateValueInChargerValues('config.power.dlm_grid_u_min', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={t('charger_VoltageForStartChargingIncreaseChargingCurrent')} width={"max(19vw, 243.2px)"} value={chargerValues.config.power.dlm_grid_u_max.toString()} onInput={value => updateValueInChargerValues('config.power.dlm_grid_u_max', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={t('charger_DelayAfterIncreasingChagingCurrent')} width={"max(19vw, 243.2px)"} value={chargerValues.config.power.dlm_grid_t_up.toString()} onInput={value => updateValueInChargerValues('config.power.dlm_grid_t_up', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={t('charger_DelayAfterDecreasingChagingCurrent')} width={"max(19vw, 243.2px)"} value={chargerValues.config.power.dlm_grid_t_dn.toString()} onInput={value => updateValueInChargerValues('config.power.dlm_grid_t_dn', parseInt(value) || 0)} />
              </>}
            </>}
            <WhiteSpace height="64px" />
          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSavePowerOptionChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingPowerOptions(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}





      {isShowingConnections && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingConnections(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingConnections(false)} />
            </ModalCloseButtonContainer>
            {isShowingConnections === true && (
              <ModalTitle>{t('charger_connections')}</ModalTitle>
            )}
            {typeof isShowingConnections == 'string' && (
              <div style={{ display: 'flex' }}>
                <Icon path="general.backArrow" width="22px" height="22px" marginright="8px" onClick={() => setIsShowingConnections(true)} />
                {isShowingConnections == 'wifi' && (
                  <ModalSubcategoryTitle>{t('charger_wifi')}</ModalSubcategoryTitle>
                )}
                {isShowingConnections == 'ethernet' && (
                  <ModalSubcategoryTitle>{t('charger_ethernet')}</ModalSubcategoryTitle>
                )}
                {isShowingConnections == 'gsm' && (
                  <ModalSubcategoryTitle>{t('charger_cellular')}</ModalSubcategoryTitle>
                )}
              </div>
            )}
            <WhiteSpace height="34px" />
            {isShowingConnections === true && (
              <>
                <SettingContainer $color="gray_300" onClick={() => setIsShowingConnections('wifi')}>
                  <SettingIconContainer $small>
                    <Icon path="settings.wifi" color="primary" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{`${t('charger_wifi')} ${t('charger_settings')}`}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                  {chargerValues.rsp_stat.wifi?.ip && (
                    <ConnectionActiveLabel>{t('charger_active')}</ConnectionActiveLabel>
                  )}
                </SettingContainer>
                <SettingContainer $color="gray_300" onClick={() => setIsShowingConnections('ethernet')}>
                  <SettingIconContainer $small>
                    <Icon path="settings.ethernet" color="primary" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{`${t('charger_ethernet')} ${t('charger_settings')}`}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                  {chargerValues.rsp_stat.ethernet?.ip_address && (
                    <ConnectionActiveLabel>{t('charger_active')}</ConnectionActiveLabel>
                  )}
                </SettingContainer>
                <SettingContainer $color="gray_300" onClick={() => setIsShowingConnections('gsm')}>
                  <SettingIconContainer $small>
                    <Icon path="settings.cellular" color="primary" width="100%" height="100%" />
                  </SettingIconContainer>
                  <SettingLabel>{`${t('charger_cellular')} ${t('charger_settings')}`}</SettingLabel>
                  <SettingArrow>
                    <Icon path="general.forwardArrow" width="100%" height="100%" />
                  </SettingArrow>
                  {chargerValues.rsp_stat.gsm && (
                    <ConnectionActiveLabel>{t('charger_active')}</ConnectionActiveLabel>
                  )}
                </SettingContainer>
              </>
            )}
            {isShowingConnections == 'wifi' && (
              <>
                {[
                  { label: 'wifiName', value: chargerValues.rsp_stat.wifi?.ssid || '-' },
                  { label: 'wifiSignalstrength', value: (chargerValues.rsp_stat.wifi?.signal_percent ?? '-') + ' %' },
                  { label: 'rssi', value: (chargerValues.rsp_stat.wifi?.rssi || '-') + ' dBm' },
                  { label: 'ipAddress', value: chargerValues.rsp_stat.wifi?.ip || '-' },
                  { label: 'macAddress', value: chargerValues.rsp_stat.wifi?.mac || '-' },
                  { label: 'wifiSsidAccessPoint', value: chargerValues.rsp_stat.wifi?.ap_mode_ssid || '-' },
                  {
                    label: 'mode', value: (() => {
                      switch (chargerValues.rsp_stat.wifi?.mode) {
                        case 0: return t('charger_connectionsSettings_off')
                        case 1: return t('charger_connectionsSettings_accessPoint')
                        case 2: return t('charger_connectionsSettings_client')
                        default: return '-'
                      }
                    })()
                  },
                  { label: 'frequency', value: chargerValues.rsp_stat.wifi?.freq || '-' },
                  { label: 'rxBitrate', value: chargerValues.rsp_stat.wifi?.rx_bitrate || '-' },
                  { label: 'txBitrate', value: chargerValues.rsp_stat.wifi?.tx_bitrate || '-' },
                  { label: 'hiddenSsid', value: chargerValues.rsp_stat.wifi?.hidden_ssid != undefined ? (chargerValues.rsp_stat.wifi.hidden_ssid ? t('general_on') : t('general_off')) : '-' },
                  { label: 'hideAp', value: chargerValues.rsp_stat.wifi?.hide_ap != undefined ? (chargerValues.rsp_stat.wifi.hide_ap ? t('general_on') : t('general_off')) : '-' },
                ].map(row => (
                  <div style={{ display: 'flex', marginBottom: '24px' }}>
                    <ModalDetailTitle>{t(`charger_connectionsSettings_${row.label}`)}</ModalDetailTitle>
                    <ModalDetailTitleThin style={{ position: 'absolute', right: '24px' }}>{row.value}</ModalDetailTitleThin>
                  </div>
                ))}
              </>
            )}
            {isShowingConnections == 'ethernet' && (
              <>
                {[
                  { label: 'networkHostname', value: chargerValues.rsp_stat.ethernet?.hostname || '-' },
                  { label: 'ipAddress', value: chargerValues.rsp_stat.ethernet?.ip_address || '-' },
                  { label: 'macAddress', value: chargerValues.rsp_stat.ethernet?.mac_address || '-' },
                  { label: 'netMask', value: chargerValues.rsp_stat.ethernet?.netmask || '-' },
                  { label: 'gateway', value: chargerValues.rsp_stat.ethernet?.gateway || '-' },
                  { label: 'dns', value: chargerValues.rsp_stat.ethernet?.dns || '-' },
                  { label: 'speed', value: chargerValues.rsp_stat.ethernet?.speed_mbps ? `${chargerValues.rsp_stat.ethernet.speed_mbps} Mbps` : '-' },
                  {
                    label: 'mode', value: (() => {
                      switch (chargerValues.rsp_stat.ethernet?.mode) {
                        case 0: return t('charger_connectionsSettings_default')
                        case 1: return t('charger_connectionsSettings_dhcp')
                        case 2: return t('charger_connectionsSettings_staticIp')
                        default: '-'
                      }
                    })()
                  }
                ].map(row => (
                  <div style={{ display: 'flex', marginBottom: '24px' }}>
                    <ModalDetailTitle>{t(`charger_connectionsSettings_${row.label}`)}</ModalDetailTitle>
                    <ModalDetailTitleThin style={{ position: 'absolute', right: '24px' }}>{row.value}</ModalDetailTitleThin>
                  </div>
                ))}
              </>
            )}
            {isShowingConnections == 'gsm' && (
              <>
                {[
                  {
                    label: 'mode', value: (() => {
                      switch (chargerValues.rsp_stat.gsm?.mode) {
                        case 0: return t('charger_connectionsSettings_off')
                        case 1: return t('charger_connectionsSettings_backup')
                        case 2: return t('charger_connectionsSettings_primary')
                        default: '-'
                      }
                    })()
                  },
                  { label: 'status', value: chargerValues.rsp_stat.gsm?.status || '-' },
                  { label: 'imeiNumber', value: chargerValues.rsp_stat.gsm?.imei || '-' },
                  { label: 'iccidNumber', value: chargerValues.rsp_stat.gsm?.iccid || '-' },
                  { label: 'uptime', value: chargerValues.rsp_stat.gsm?.uptime || '-' },
                  { label: 'rssi', value: chargerValues.rsp_stat.gsm?.rssi || '-' },
                  { label: 'receivedBytes', value: chargerValues.rsp_stat.gsm?.rx_bytes || '-' },
                  { label: 'transferedBytes', value: chargerValues.rsp_stat.gsm?.tx_bytes || '-' },
                  { label: 'operator', value: chargerValues.rsp_stat.gsm?.operator || '-' }
                ].map(row => (
                  <div style={{ display: 'flex', marginBottom: '24px' }}>
                    <ModalDetailTitle>{t(`charger_connectionsSettings_${row.label}`)}</ModalDetailTitle>
                    <ModalDetailTitleThin style={{ position: 'absolute', right: '24px' }}>{row.value}</ModalDetailTitleThin>
                  </div>
                ))}
              </>
            )}
          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={() => setIsShowingConnections(false)}>{t('charger_close')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}





      {isShowingOcpp && <OcppModal chargerId={charger.chargerId} onClose={() => setIsShowingOcpp(false)} />}
      {isShowingPowerShareStatistics && <PowerShareStatisticsModal chargerId={charger.chargerId} onClose={() => setIsShowingPowerShareStatistics(false)} />}
      {isShowingIntegrations && <IntegrationsModal chargerId={charger.chargerId} onClose={() => setIsShowingIntegrations(false)} />}

      {isShowingMidMetering && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingMidMetering(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingMidMetering(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_midMetering')}</ModalTitle>
            <WhiteSpace height="34px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_midMeterEnabled')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.power.mid_meter_en} setToggled={(value) => updateValueInChargerValues('config.power.mid_meter_en', value)} />
              </div>
            </div>
            {chargerValues.config.power.mid_meter_en && (
              <>
                <WhiteSpace height="8px" />
                <ModalDetailDivider $isColor />
                <div style={{ display: 'flex' }}>
                  <ModalDetailTitle>{t('charger_midMeterState')}</ModalDetailTitle>
                  <ModalDetailTitleThin style={{ position: 'absolute', right: '24px' }}>{(() => {
                    switch (chargerValues.config.power.mid_meter_state) {
                      case 0: return t('charger_dlmMidStates_UNAVAILABLE_ERROR')
                      case 1: return t('charger_dlmMidStates_UNAVAILABLE')
                      case 2: return t('charger_dlmMidStates_CONNECTED_NOT_IN_USE')
                      case 3: return t('charger_dlmMidStates_CONNECTED_IN_USE')
                    }
                  })()}</ModalDetailTitleThin>
                </div>
                <WhiteSpace height="18px" />
                <div style={{ display: 'flex' }}>
                  <ModalDetailTitle>{t('charger_midMeterType')}</ModalDetailTitle>
                  <div style={{ position: 'absolute', right: '50px', width: '52%' }}>
                    <Select width={"100%"} options={[MID_METER_TYPE_MAP.get(chargerValues.config.power.mid_meter_type) ?? "-", ...MID_METER_TYPE_MAP.values()]} handleSelect={(value) => { updateValueInChargerValues('config.power.mid_meter_type', [...MID_METER_TYPE_MAP].find(([, v]) => v === value)?.[0] ?? 0) }} />
                  </div>
                </div>
              </>
            )}
          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSaveMidMeteringChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingMidMetering(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}








      {isShowingIpMeterSettings && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingIpMeterSettings(false)}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingIpMeterSettings(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_ipMeterSettings')}</ModalTitle>
            <WhiteSpace height="34px" />
            <ModalDetailTitle>{"Base Settings"}</ModalDetailTitle>
            <WhiteSpace height="8px" />
            <InputWithLabel label={"IP Address"} width={"100%"} value={chargerValues.config.ip_485.ip} onInput={(value) => updateValueInChargerValues('config.ip_485.ip', value)} />
            <WhiteSpace height="8px" />
            <InputWithLabel label={"Port"} width={"100%"} value={chargerValues.config.ip_485.port.toString()} onInput={(value) => updateValueInChargerValues('config.ip_485.port', parseInt(value) || 0)} />
            <ModalDetailDivider $isColor />

            <ModalDetailTitle>{"Meter 1"}</ModalDetailTitle>
            <WhiteSpace height="18px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitleThin>{"Meter Type"}</ModalDetailTitleThin>
              <div style={{ position: 'absolute', right: '50px', width: '52%' }}>
                <Select width={"100%"} options={[METER_TYPE_OPTIONS[chargerValues.config.ip_485.meter1.type], ...METER_TYPE_OPTIONS]} handleSelect={(value) => { updateValueInChargerValues('config.ip_485.meter1.type', METER_TYPE_OPTIONS.indexOf(value)) }} />
              </div>
            </div>
            <WhiteSpace height="18px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitleThin>{"Usage Type"}</ModalDetailTitleThin>
              <div style={{ position: 'absolute', right: '50px', width: '52%' }}>
                <Select width={"100%"} options={[METER_USAGE_OPTIONS[chargerValues.config.ip_485.meter1.usage], ...METER_USAGE_OPTIONS]} handleSelect={(value) => { updateValueInChargerValues('config.ip_485.meter1.usage', METER_USAGE_OPTIONS.indexOf(value)) }} />
              </div>
            </div>
            <WhiteSpace height="18px" />
            <InputWithLabel label={"Serial Number"} width={"100%"} value={chargerValues.config.ip_485.meter1.sn} onInput={(value) => updateValueInChargerValues('config.ip_485.meter1.sn', value)} />

            <ModalDetailDivider $isColor />

            <ModalDetailTitle>{"Meter 2"}</ModalDetailTitle>
            <WhiteSpace height="18px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitleThin>{"Meter Type"}</ModalDetailTitleThin>
              <div style={{ position: 'absolute', right: '50px', width: '52%' }}>
                <Select width={"100%"} options={[METER_TYPE_OPTIONS[chargerValues.config.ip_485.meter2.type], ...METER_TYPE_OPTIONS]} handleSelect={(value) => { updateValueInChargerValues('config.ip_485.meter2.type', METER_TYPE_OPTIONS.indexOf(value)) }} />
              </div>
            </div>
            <WhiteSpace height="18px" />
            <div style={{ display: 'flex' }}>
              <ModalDetailTitleThin>{"Usage Type"}</ModalDetailTitleThin>
              <div style={{ position: 'absolute', right: '50px', width: '52%' }}>
                <Select width={"100%"} options={[METER_USAGE_OPTIONS[chargerValues.config.ip_485.meter2.usage], ...METER_USAGE_OPTIONS]} handleSelect={(value) => { updateValueInChargerValues('config.ip_485.meter2.usage', METER_USAGE_OPTIONS.indexOf(value)) }} />
              </div>
            </div>
            <WhiteSpace height="18px" />
            <InputWithLabel label={"Serial Number"} width={"100%"} value={chargerValues.config.ip_485.meter2.sn} onInput={(value) => updateValueInChargerValues('config.ip_485.meter2.sn', value)} />


          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSaveIpMeterSettingsChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingIpMeterSettings(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}








      {isShowingPowerShareSettings && chargerValues && (
        <Modal align="right" onEscape={() => setIsShowingPowerShareSettings(false)}>
          <ModalContainer style={{ position: 'relative', overflowY: 'scroll', overflowX: 'hidden' }}>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsShowingPowerShareSettings(false)} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_powerShareSettings')}</ModalTitle>
            <WhiteSpace height="34px" />





            <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px' }}>
              <ModalDetailTitle>{t('charger_deviceSettings')}</ModalDetailTitle>
              <WhiteSpace height="24px" />
              <div style={{ display: 'flex' }}>
                <ModalDetailTitle>{t('charger_deviceRole')}</ModalDetailTitle>
                <div style={{ position: 'absolute', right: '58px', width: '50%' }}>
                  <Select width={"100%"} options={[PSDeviceRoleTranslationTable[chargerValues.config.pi2pi_netw.role.toString()], ...Object.values(PSDeviceRoleTranslationTable)]} handleSelect={(value) => { updateValueInChargerValues('config.pi2pi_netw.role', parseInt(Object.keys(PSDeviceRoleTranslationTable).find(k => PSDeviceRoleTranslationTable[k] === value) || "0")) }} />
                </div>
              </div>
              <WhiteSpace height="18px" />
              <div style={{ display: 'flex' }}>
                <ModalDetailTitle>{t('charger_phaseOrder')}</ModalDetailTitle>
                <div style={{ position: 'absolute', right: '58px', width: '50%' }}>
                  <Select width={"100%"} options={[PSPhaseOrderTranslationTable[chargerValues.config.power.phase_order.toString()], ...Object.values(PSPhaseOrderTranslationTable)]} handleSelect={(value) => { updateValueInChargerValues('config.power.phase_order', parseInt(Object.keys(PSPhaseOrderTranslationTable).find(k => PSPhaseOrderTranslationTable[k] === value) || "0")) }} />
                </div>
              </div>
              <WhiteSpace height="22px" />
              <InputWithLabel label={t('charger_autonomCurrent')} width={"100%"} value={chargerValues.config.pi2pi_netw.ps_autonom_curr.toString()} onInput={value => updateValueInChargerValues('config.pi2pi_netw.ps_autonom_curr', parseFloat(value) || 0)} />
              <WhiteSpace height="8px" />
              <InputWithLabel label={t('charger_chargerPort')} width={"100%"} value={chargerValues.config.pi2pi_netw.port.toString()} onInput={value => updateValueInChargerValues('config.pi2pi_netw.port', parseInt(value) || "")} />
              <WhiteSpace height="8px" />
              <InputWithLabel label={t('charger_chargerSegmentId')} width={"100%"} value={chargerValues.config.pi2pi_netw.segment_id.toString()} onInput={value => updateValueInChargerValues('config.pi2pi_netw.segment_id', parseInt(value) || 0)} />
              <WhiteSpace height="8px" />
              <InputWithLabel label={`${t('charger_master')} IP`} width={"100%"} value={chargerValues.config.pi2pi_netw.master1_ip} onInput={value => updateValueInChargerValues('config.pi2pi_netw.master1_ip', value)} />
              <WhiteSpace height="8px" />
              <InputWithLabel label={`${t('charger_master')} 2 IP`} width={"100%"} value={chargerValues.config.pi2pi_netw.master2_ip} onInput={value => updateValueInChargerValues('config.pi2pi_netw.master2_ip', value)} />
              <WhiteSpace height="8px" />
              <InputWithLabel label={`${t('charger_topMaster')} IP`} width={"100%"} value={chargerValues.config.pi2pi_netw.top_master1_ip} onInput={value => updateValueInChargerValues('config.pi2pi_netw.top_master1_ip', value)} />
              <WhiteSpace height="8px" />
              <InputWithLabel label={`${t('charger_topMaster')} 2 IP`} width={"100%"} value={chargerValues.config.pi2pi_netw.top_master2_ip} onInput={value => updateValueInChargerValues('config.pi2pi_netw.top_master2_ip', value)} />
            </div>
            <WhiteSpace height="24px" />








            <div style={{ display: 'flex' }}>
              <ModalDetailTitle>{t('charger_powerShare')}</ModalDetailTitle>
              <div style={{ position: 'absolute', right: '20px' }}>
                <Switch toggled={chargerValues.config.pi2pi_netw.ps_enabled} setToggled={(value) => updateValueInChargerValues('config.pi2pi_netw.ps_enabled', value)} />
              </div>
            </div>
            {chargerValues.config.pi2pi_netw.ps_enabled && <>
              <WhiteSpace height="24px" />

              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px' }}>
                <ModalDetailTitle>{t('charger_baseOptions')}</ModalDetailTitle>
                <WhiteSpace height="24px" />
                <div style={{ display: 'flex' }}>
                  <ModalDetailTitle>{t('charger_powerShareType')}</ModalDetailTitle>
                  <div style={{ position: 'absolute', right: '58px', width: '50%' }}>
                    <Select width={"100%"} options={[PSTypeTranslationTable[chargerValues.ps_conf.ps_general.ps_method.toString()], ...Object.values(PSTypeTranslationTable)]} handleSelect={(value) => { updateValueInChargerValues('ps_conf.ps_general.ps_method', parseInt(Object.keys(PSTypeTranslationTable).find(k => PSTypeTranslationTable[k] === value) || "0")) }} />
                  </div>
                </div>
                <WhiteSpace height="8px" />
              </div>

              {(chargerValues.ps_conf.ps_general.ps_method == 1 || chargerValues.ps_conf.ps_general.ps_method == 2) && <>
                <WhiteSpace height="8px" />
                <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px' }}>
                  <ModalDetailTitle>{t('charger_externalMeterAccessibility')} <span style={{ fontSize: '11px', fontWeight: 500, color: '#7E8299' }}>ext_meter</span></ModalDetailTitle>
                  <WhiteSpace height="24px" />
                  <InputWithLabel label={`R ${t('charger_phaseMaxTotalAmper')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.imax_r.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.imax_r', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`S ${t('charger_phaseMaxTotalAmper')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.imax_s.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.imax_s', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`T ${t('charger_phaseMaxTotalAmper')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.imax_t.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.imax_t', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`R ${t('charger_phaseReserveAmper')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.reserve_r.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.reserve_r', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`S ${t('charger_phaseReserveAmper')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.reserve_s.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.reserve_s', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`T ${t('charger_phaseReserveAmper')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.reserve_t.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.reserve_t', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`R ${t('charger_phaseAllowedAmperDuringIpMeterFailure')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.i_offline_r.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.i_offline_r', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`S ${t('charger_phaseAllowedAmperDuringIpMeterFailure')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.i_offline_s.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.i_offline_s', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                  <InputWithLabel label={`T ${t('charger_phaseAllowedAmperDuringIpMeterFailure')}`} width={"100%"} value={chargerValues.ps_conf.ext_meter.i_offline_t.toString()} onInput={value => updateValueInChargerValues('ps_conf.ext_meter.i_offline_t', parseInt(value) || 0)} />
                  <WhiteSpace height="8px" />
                </div>
              </>}

              <WhiteSpace height="8px" />

              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px' }}>
                <ModalDetailTitle>{t('charger_chargerOptions')} <span style={{ fontSize: '11px', fontWeight: 500, color: '#7E8299' }}>ps_general</span></ModalDetailTitle>
                <WhiteSpace height="24px" />
                <InputWithLabel label={t('charger_numberOfClients')} width={"100%"} value={chargerValues.ps_conf.ps_general.n_endpoints.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.n_endpoints', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={t('charger_reservedAmperForNotAvailableClients')} width={"100%"} value={chargerValues.ps_conf.ps_general.reserve_amps_each_na.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.reserve_amps_each_na', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`Minimum R ${t('charger_phaseReservedAmper')}`} width={"100%"} value={chargerValues.ps_conf.ps_general.reserve_r.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.reserve_r', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`Minimum S ${t('charger_phaseReservedAmper')}`} width={"100%"} value={chargerValues.ps_conf.ps_general.reserve_s.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.reserve_s', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`Minimum T ${t('charger_phaseReservedAmper')}`} width={"100%"} value={chargerValues.ps_conf.ps_general.reserve_t.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.reserve_t', parseInt(value) || 0)} />
              </div>

              <WhiteSpace height="8px" />

              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px' }}>
                <ModalDetailTitle>{t('charger_systemAmper')} <span style={{ fontSize: '11px', fontWeight: 500, color: '#7E8299' }}>ps_general</span></ModalDetailTitle>
                <WhiteSpace height="24px" />
                <InputWithLabel label={`R ${t('charger_phaseMaxSumAmper')}`} width={"100%"} value={chargerValues.ps_conf.ps_general.imax_r.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.imax_r', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`S ${t('charger_phaseMaxSumAmper')}`} width={"100%"} value={chargerValues.ps_conf.ps_general.imax_s.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.imax_s', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`T ${t('charger_phaseMaxSumAmper')}`} width={"100%"} value={chargerValues.ps_conf.ps_general.imax_t.toString()} onInput={value => updateValueInChargerValues('ps_conf.ps_general.imax_t', parseInt(value) || 0)} />
              </div>

              <WhiteSpace height="8px" />

              <div style={{ backgroundColor: 'white', padding: '12px', borderRadius: '8px' }}>
                <ModalDetailTitle>{t('charger_l2Configuration')} <span style={{ fontSize: '11px', fontWeight: 500, color: '#7E8299' }}>l2</span></ModalDetailTitle>
                <WhiteSpace height="24px" />
                <InputWithLabel label={`R ${t('charger_phaseAutonomousAmper')}`} width={"100%"} value={chargerValues.ps_conf.l2.i_autonom_r.toString()} onInput={value => updateValueInChargerValues('ps_conf.l2.i_autonom_r', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`S ${t('charger_phaseAutonomousAmper')}`} width={"100%"} value={chargerValues.ps_conf.l2.i_autonom_s.toString()} onInput={value => updateValueInChargerValues('ps_conf.l2.i_autonom_s', parseInt(value) || 0)} />
                <WhiteSpace height="8px" />
                <InputWithLabel label={`T ${t('charger_phaseAutonomousAmper')}`} width={"100%"} value={chargerValues.ps_conf.l2.i_autonom_t.toString()} onInput={value => updateValueInChargerValues('ps_conf.l2.i_autonom_t', parseInt(value) || 0)} />
              </div>
            </>}

            <WhiteSpace height="128px" />
          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleSavePowerShareSettingsChanges} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => setIsShowingPowerShareSettings(false)}>{t('charger_cancel')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}






      {isShowingDebugLevels && (
        <Modal align="right" onEscape={() => { setIsShowingDebugLevels(false); setDebugLevels(null) }}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => { setIsShowingDebugLevels(false); setDebugLevels(null) }} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_debugLevels')}</ModalTitle>
            <WhiteSpace height="8px" />

            {(debugLevels && debugLevels.length > 0) ? <>
              <Table id="Settings_DebugLevels" height="calc(85vh - 90px)" columns={[
                { width: '5%' },
                { label: 'Name', width: '45%' },
                { label: 'Level', width: '50%' }
              ]} data={debugLevels.map((debugLevel, index) => [
                {
                  custom: <></>
                }, {
                  value: {
                    type: TableCellTypes.Label,
                    value: debugLevel.name
                  }
                }, {
                  custom: <Select width='80%' options={[['1 - low', '2 - medium', '3 - high', '4 - very high'][debugLevel.value - 1], ...['1 - low', '2 - medium', '3 - high', '4 - very high']]} handleSelect={(value) => handleUpdateDebugLevel(index, parseInt(value[0]))} isAbsolute={true} />
                }
              ])} />
            </> : <p style={{ width: '100%', textAlign: 'center', fontWeight: '700' }}><LoaderDots /></p>}

          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={() => { setDebugLevels(null); setTimeout(handleResetDebugLevelsToDefault, 300) }}>{t('charger_reset')}</ModalButton>
            <ModalButton onClick={() => { setDebugLevels(null); setTimeout(fetchCMD_GET_DEBUG_LEVEL, 300) }} $hasBg>{t('charger_refresh')}</ModalButton>
            <ModalButton onClick={() => { setIsShowingDebugLevels(false); setDebugLevels(null) }} $hasBg>{t('charger_close')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}









      {isShowingDiagnosticLogConfig && (
        <Modal align="center" onEscape={() => { setIsShowingDiagnosticLogConfig(false) }}>
          <ModalContainer>
            <ModalCloseButtonContainer>
              <Icon path="general.x" width="20px" height="20px" onClick={() => { setIsShowingDiagnosticLogConfig(false) }} />
            </ModalCloseButtonContainer>
            <ModalTitle>{t('charger_diagnosticLogConfig')}</ModalTitle>
            <WhiteSpace height="8px" />

            {diagnosticLogConfig ? <>
              <WhiteSpace height="12px" />
              <ModalTableScroller>
              <table style={{ width: '100%', fontWeight: '600', fontSize: 14 }}>
                <tr>
                  <td>
                    <p style={{ width: '150px' }}>name</p>
                  </td>
                  <td>
                    <p style={{ width: '150px' }} title="logolás be/kikapcsolt állapota">enabled</p>
                  </td>
                  <td>
                    <p style={{ width: '150px' }} title="10 perc elteltét jelző sor automatikus beszúrása">tick10min</p>
                  </td>
                  <td>
                    <p style={{ width: '300px' }} title="[unix time] logolás aut. kikapcsolása adott dátummal; 0:tiltva">dt_off</p>
                  </td>
                  <td>
                    <p style={{ width: '150px' }} title="10 perces intervallumban maximális log sorok száma">max_lines_10min</p>
                  </td>
                  <td>
                    <p style={{ width: '150px' }}>param1</p>
                  </td>
                  <td>
                    <p style={{ width: '150px' }}>param2</p>
                  </td>
                </tr>
              </table>
              </ModalTableScroller>
              {diagnosticLogConfig.map(logConfig => (
                <table style={{ width: '100%', display: 'flex', fontSize: 14 }}>
                  <tr>
                    <td>
                      <p style={{ width: '150px' }}>{logConfig.name}</p>
                    </td>
                    <td style={{ width: '150px' }}>
                      <Switch toggled={logConfig.enabled} setToggled={(value) => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, enabled: value } : item))} />
                    </td>
                    <td style={{ width: '150px' }}>
                      <Switch toggled={logConfig.tick10min} setToggled={(value) => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, tick10min: value } : item))} />
                    </td>
                    <td style={{ width: '300px' }}>
                      <div style={{ display: 'flex' }}>
                        <Input width={"50%"} placeholder="" value={logConfig.dt_off.toString()} onInput={(value) => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, dt_off: parseInt(value) || 0 } : item))} />
                        <WhiteSpace width="4px" />
                        <div title="4 óra"><Button label={"4ó"} width={"30px"} onclick={() => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, dt_off: Math.floor(Date.now() / 1000) + 14400 } : item))} /></div>
                        <WhiteSpace width="4px" />
                        <div title="1 nap"><Button label={"1n"} width={"30px"} onclick={() => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, dt_off: Math.floor(Date.now() / 1000) + 86400 } : item))} /></div>
                        <WhiteSpace width="4px" />
                        <div title="3 nap"><Button label={"3n"} width={"30px"} onclick={() => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, dt_off: Math.floor(Date.now() / 1000) + 259200 } : item))} /></div>
                        <WhiteSpace width="32px" />
                      </div>
                    </td>
                    <td style={{ width: '150px' }}>
                      <Input width={"50%"} placeholder="" value={logConfig.max_lines_10min.toString()} onInput={(value) => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, max_lines_10min: parseInt(value) || 0 } : item))} />
                    </td>
                    <td style={{ width: '150px' }}>
                      <Input width={"50%"} placeholder="" value={logConfig.param1.toString()} onInput={(value) => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, param1: parseInt(value) || 0 } : item))} />
                    </td>
                    <td style={{ width: '150px' }}>
                      <Input width={"50%"} placeholder="" value={logConfig.param2.toString()} onInput={(value) => setDiagnosticLogConfig(prev => prev && prev.map(item => item.name === logConfig.name ? { ...item, param2: parseInt(value) || 0 } : item))} />
                    </td>
                  </tr>
                </table>
              ))}
            </> : <>
              <p style={{ width: '100%', textAlign: 'center', fontWeight: '700' }}><LoaderDots /></p>
            </>}

          </ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={() => window.open('https://voltie.atlassian.net/wiki/spaces/VOLTIE/pages/970227713/R+szletes+m+r+sek+r+szletes+logol+s', '_blank')} style={{ position: 'absolute', left: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon path="general.externalLink" width="16px" height="16px" color="gray_600" />
                Docs
              </div>
            </ModalButton>
            <ModalButton onClick={handleSaveDiagnosticLogConfig} $hasBg>{t('charger_save')}</ModalButton>
            <ModalButton onClick={() => { setIsShowingDiagnosticLogConfig(false) }}>{t('charger_close')}</ModalButton>
          </ModalButtonContainer>
        </Modal>
      )}
    </>
  )
}